import React from "react";
import { useState, useEffect, useRef } from "react";
import { Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

import { useDispatch, useSelector } from "react-redux";
// Importing Redux slices
import { useUser } from "./features/user/userSlice";
import { selectUser, selectToken } from "./features/user/userSlice";
import { useAppSelector, useAppDispatch } from "./app/hooks";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { ResultTableModal } from "./components/modals/ResultTableModal";
import { SXSurveyDialog } from "@common/components/ui-components/SXSurveyDialog";
// Importing Redux store and types
import { RootState } from "./app/store";
import {
  Accordion,
  Button,
  ButtonGroup,
  ButtonToolbar,
  Col,
  Container,
  Dropdown,
  ListGroup,
  Nav,
  Navbar,
  OverlayTrigger,
  Row,
  Tab,
  Tabs,
  Tooltip,
} from "react-bootstrap";

// Importing components
import SXMobileWarning from "@common/components/ui-components/SXMobileWarning";
import { SXOffCanvasMenu } from "./components/SXOffCanvasMenu";
import { TableColumn } from "@common/components/ui-components/SXTable";
import { SXEditField } from "@common/components/ui-components/SXEditField";
import { NGDropdownListItem, SXDropdown, SXDropdownSection } from "@common/components/ui-components/SXDropdown";
import { AlertMessage, clearAlerts } from "./helpers/AlertMessage";
import { Viewport } from "./components/Viewport";
import type { GraphPointsMode } from "./components/Viewport";
import { SettingsModal } from "./components/modals/SettingsModal";
import { SXCodeSettings } from "./components/SXProjectCodeSettings";
import { SXInputGeometry } from "./components/SXInputGeometry";
import { SXProjectInputLoadCases } from "./components/SXProjectInputLoadCases";
import { SXBuckling } from "./components/SXBuckling";
import { SXInputCalculation } from "./components/SXInputCalculation";
import { SXInputFireSettings } from "./components/SXInputFireSettings";
import { SXInputReinforcement } from "./components/SXInputReinforcement";
import { SXInputLoads } from "./components/SXInputLoads";
import { checkSecondOrderVerticalPointLoad } from "./features/projects/secondOrderWarnings";
import { ProjectInputSectionExplorer } from "./components/SXInputSectionAndMaterial";
import { AdminViewWrapperModal } from "./components/modals/AdminViewWrapperModal";
import {
  activeLCOs,
  selectProject,
  selectAnalysisStatus,
  selectCodeCheckStatus,
  selectDesignStatus,
  selectHasAnalysisOutput,
  selectHasCodeCheckOutput,
  selectCalcError,
  updateProject,
  selectSecondOrder,
  ProjectSlice,
  selectCarbonStatus,
} from "./features/projects/projectSlice";
import { selectConfig } from "./features/config/configSlice";
import { byLimitState, isSLS, isULS, SymbolName } from "@common/helpers/funlib";
import { getClientId } from "@common/helpers/funlib";

// Importing hooks
import { toggleFullscreenThunk } from "./features/ui/uiThunks";

// Importing actions and selectors from the UI slice
import {
  openModal,
  closeModal,
  selectModalById,
  selectShowOffCanvas,
  openOffCanvas,
  closeOffCanvas,
  toggleOffCanvas,
  selectFullscreen,
  selectIsMobile,
  setIsMobile,
  selectBreakPoint,
  selectActiveAccordionKeys,
  setAccordionKeys,
  toggleAccordionKey,
  toggleCollapseAllAccordion,
  setViewportState,
  selectViewportState,
  toggleShowSectionDimensions,
  selectShowSectionDimensions,
  toggleShowSectionBuckling,
  setShowSectionBuckling,
  toggleShowSectionReactions,
  selectShowSectionReactions,
  selectShowSectionBuckling,
  toggleShowSectionFireIndicator,
  selectShowSectionFireIndicator,
  setShowSectionFireIndicator,
  selectCriticalLoadExceeded,
  setCriticalLoadExceeded,
} from "./features/ui/uiSlice";

// logo & icon imports
import { SimplexSvgLogo } from "./assets/SimplexSvgLogo";
import { UserInitialsIcon } from "./assets/UserInitialsIcon";
import {
  FullScreenToggleIcon,
  CalculatorIcon,
  CheckMarkIcon,
  EqualsIcon,
  PlayIcon,
  PrintIcon,
  SettingsIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SplitViewIcon,
  ViewIcon,
  Cube5,
} from "@sx-icons";
import { projects, useProject } from "./features/projects/projectSlice";
import { SaveAsModal } from "./components/modals/SaveAsModal";
import { ng } from "@common/ngds";

import {
  IStageDictionary,
  IReactionForce,
  IReactionForceDictionary,
  ISectionForce,
  ISectionForceDictionary,
  IDisplacement,
  IDisplacementDictionary,
  IDesignResult,
  IDesignResultDictionary,
  AppType,
} from "@common/types/sharedTypes";
import { getLoadcomb } from "@common/helpers/protoGen";
import { SXProjectInputLoadCombinations } from "./components/SXInputLoadCombinations";
import { JSONDebugPrint } from "@common/helpers/JSONDebugPrint";
import { ControlsReport } from "./components/SXRcControlsReport";
import { MemberDesignPanel } from "./components/SXMemberDesignPanel";
import { SXCalcProgressOverlay } from "./components/SXCalcProgressOverlay";
import { SXCarbonFootprint } from "@common/components/ui-components/SXCarbonFootprint";
import { SX3DViewSettings } from "./components/SX3DViewSettings";
import { LCOGeneratoreModal, LCOGeneratorModalId } from "./components/modals/LCOGeneratorModal";
import { report_exception } from "./components/ui-components/SXAlert/SXAlertMessage";
import { DisclaimerModal } from "@common/components/ui-components/Modales/DisclaimerModel";
import { ReleaseNotesModal } from "./components/modals/ReleaseNotesModal";
import { IReleaseNotes } from "@common/types/sharedTypes";

const SURVEY_ID = "9fc0b4fa993ef115";
const SURVEY_DELAY_MS = 2 * 60 * 1000;

type MyRowData = {
  id: string;
  name: string;
  value: number;
  value2?: number;
};

export const useDevConfig = (): boolean => {
  const config = useAppSelector(selectConfig);
  return config?.IS_DEV ?? false;
};

const getActivePositionResults = (
  position: ng.result.PositionResult,
  showSecOrder: boolean,
  show1stOrd_noBow: boolean,
) => (showSecOrder ? position.results2nd : show1stOrd_noBow ? position.results : position.resultsBowImp);

const getForceValueIndex = (force: ng.result.ForceData | null | undefined, forceType: ng.result.Force) => {
  if (!force) {
    return -1;
  }

  return force.type.findIndex((type) => type === forceType);
};

const getDisplacementValueIndex = (
  displacement: ng.result.DisplacementData | null | undefined,
  displacementType: ng.result.Displacement,
) => {
  if (!displacement) {
    return -1;
  }

  return displacement.type.findIndex((type) => type === displacementType);
};

const setForceValueForType = (
  position: ng.result.PositionResult,
  showSecOrder: boolean,
  show1stOrd_noBow: boolean,
  forceType: ng.result.Force,
  value: number,
) => {
  const force = getActivePositionResults(position, showSecOrder, show1stOrd_noBow)?.force;
  const index = getForceValueIndex(force, forceType);
  if (!force || index < 0 || index >= force.value.length) {
    return;
  }

  force.value[index] = value;
};

const setDisplacementValueForType = (
  position: ng.result.PositionResult,
  showSecOrder: boolean,
  show1stOrd_noBow: boolean,
  displacementType: ng.result.Displacement,
  value: number,
) => {
  const displacement = getActivePositionResults(position, showSecOrder, show1stOrd_noBow)?.displacement;
  const index = getDisplacementValueIndex(displacement, displacementType);
  if (!displacement || index < 0 || index >= displacement.value.length) {
    return;
  }

  displacement.value[index] = value;
};

const interpolateResultValue = (x1: number, x2: number, y1: number, y2: number, x: number) => {
  if (Math.abs(x1 - x2) <= 2.1e-3) {
    return y1;
  }

  return y1 + (y2 - y1) * ((x - x1) / (x2 - x1));
};

const interpolateActiveResults = (
  pos: ng.result.PositionResult,
  prevPos: ng.result.PositionResult,
  endX: number,
  x: number,
  newPos: ng.result.PositionResult,
  showSecOrder: boolean,
  show1stOrd_noBow: boolean,
) => {
  const prevResults = getActivePositionResults(prevPos, showSecOrder, show1stOrd_noBow);
  const currentResults = getActivePositionResults(pos, showSecOrder, show1stOrd_noBow);
  const newResults = getActivePositionResults(newPos, showSecOrder, show1stOrd_noBow);

  if (!prevResults || !currentResults || !newResults) {
    return;
  }

  if (prevResults.force && currentResults.force && newResults.force) {
    for (const forceType of currentResults.force.type) {
      const prevIndex = getForceValueIndex(prevResults.force, forceType);
      const currentIndex = getForceValueIndex(currentResults.force, forceType);
      const newIndex = getForceValueIndex(newResults.force, forceType);

      if (prevIndex < 0 || currentIndex < 0 || newIndex < 0) {
        continue;
      }

      const y1 = prevResults.force.value[prevIndex] ?? 0;
      const y2 = currentResults.force.value[currentIndex] ?? 0;
      newResults.force.value[newIndex] = interpolateResultValue(prevPos.getDistance() ?? 0, endX, y1, y2, x);
    }
  }

  if (prevResults.displacement && currentResults.displacement && newResults.displacement) {
    for (const displacementType of currentResults.displacement.type) {
      const prevIndex = getDisplacementValueIndex(prevResults.displacement, displacementType);
      const currentIndex = getDisplacementValueIndex(currentResults.displacement, displacementType);
      const newIndex = getDisplacementValueIndex(newResults.displacement, displacementType);

      if (prevIndex < 0 || currentIndex < 0 || newIndex < 0) {
        continue;
      }

      const y1 = prevResults.displacement.value[prevIndex] ?? 0;
      const y2 = currentResults.displacement.value[currentIndex] ?? 0;
      newResults.displacement.value[newIndex] = interpolateResultValue(prevPos.getDistance() ?? 0, endX, y1, y2, x);
    }
  }
};

function App() {
  const { t, i18n } = useTranslation();
  const { hideDisclaimerModal, saveSurveyData, scheduleSurveyPrompt, resetscheduleSurveyPrompt, markReleaseNotesSeen } =
    useUser();
  const { getProject, analyse, codeCheck, saveTempProject, secondOrder } = useProject();
  const user = useAppSelector(selectUser);
  const token = useAppSelector(selectToken);
  const selProject = useAppSelector(selectProject);
  const appConfig = useAppSelector(selectConfig);
  const isDevConfig = useDevConfig();
  const analysisStatus = useAppSelector(selectAnalysisStatus);
  const codeCheckStatus = useAppSelector(selectCodeCheckStatus);
  const designStatus = useAppSelector(selectDesignStatus);
  const calcError = useAppSelector(selectCalcError);
  // Any of the three long-running calc routes locks the rest of the UI the same way: none of
  // them may overlap with an edit, or with each other (a design run currently in flight must not
  // let the user kick off an analysis/code check against project data it is about to replace).
  const isCalculating = analysisStatus === "loading" || codeCheckStatus === "loading" || designStatus === "loading";
  const hubUrl = (appConfig as { REACT_APP_HUB_URL?: string } | null)?.REACT_APP_HUB_URL?.trim();

  const handeOpenSimplexCenter = () => {
    const fallbackHubUrl = "https://simplex-center-dev.private.strusoft.com/";
    const targetUrl = hubUrl && hubUrl.length > 0 ? hubUrl : fallbackHubUrl;
    window.open(targetUrl, "_blank", "noopener,noreferrer");
  };

  // Shared with the member design's run button, so the two calculations that need the section
  // forces are gated on the very same condition.
  const hasAnalysisOutput = useAppSelector(selectHasAnalysisOutput);
  // An analysis whose axial force reaches the critical load is treated as no analysis at all: the
  // section forces it produced are meaningless, so code check and design stay closed until changed
  // input produces usable ones (set below, where the critical_load_exceeded_ alert is raised).
  const criticalLoadExceeded = useAppSelector(selectCriticalLoadExceeded);
  const hasUsableAnalysisOutput = hasAnalysisOutput && !criticalLoadExceeded;
  const hasPersistedResults =
    !!selProject?.data?.data?.output?.lcombs?.length ||
    !!selProject?.data?.data?.output?.rc?.length ||
    !!selProject?.data?.data?.output?.steel?.length ||
    !!selProject?.data?.data?.output?.timber?.length;
  // Printing renders the persisted project, so with no results the printout is just the input the
  // user has in front of them. The bar is a code check, not an analysis: the printout is a controls
  // document, and an analysis alone fills output.lcombs with section forces carrying no controls at
  // all. So this is deliberately *not* the hasUsableAnalysisOutput gate the code check and design
  // buttons share — print opens one step later, once those controls exist. Every input change
  // prunes the output, so it closes again on the next edit.
  const hasCodeCheckOutput = useAppSelector(selectHasCodeCheckOutput);
  const printBlockedReason = hasCodeCheckOutput
    ? ""
    : t("print_needs_code_check_.label", "Run a code check before printing");
  const params = useParams();
  /** STATE HANDLERS BELOW */
  const dispatch = useAppDispatch();
  const surveyModalTimerRef = useRef<number | null>(null);
  const surveyPromptRequestedRef = useRef(false);
  //offcanvas settings
  const showOffCanvas = useSelector(selectShowOffCanvas);
  const isFullscreen = useSelector(selectFullscreen);
  const isMobile = useSelector(selectIsMobile);
  const mobileWarningBreakpoint = useSelector(selectBreakPoint);
  // this is for the accordions
  const activeAccordionKeys = useSelector(selectActiveAccordionKeys);
  const showSectionFireIndicator = useSelector(selectShowSectionFireIndicator);
  const showSectionBuckling = useSelector(selectShowSectionBuckling);
  const showSectionDimensions = useSelector(selectShowSectionDimensions);
  const showSectionReactions = useSelector(selectShowSectionReactions);
  const isFireAccordionOpen = activeAccordionKeys.includes("8");
  const isBucklingAccordionOpen = activeAccordionKeys.includes("4");
  // Modal state is handled by Redux, so we use a selector to get the modal state
  const isSettingsModalOpen = useSelector(selectModalById("settingsModal"));
  const isResultTableModalOpen = useSelector(selectModalById("resultTableModal"));
  const isSaveModalOpen = useSelector(selectModalById("saveAsModal"));
  const isAdminViewModalOpen = useSelector(selectModalById("adminViewModal"));
  const isDisclaimerModalOpen = useSelector(selectModalById("disclaimerModal"));
  const isSurveyModalOpen = useSelector(selectModalById("surveyModal"));
  const isReleaseNotesModalOpen = useSelector(selectModalById("releaseNotesModal"));
  const isLatestReleaseNotesModalOpen = useSelector(selectModalById("latestReleaseNotesModal"));

  const isLcoModalOpen = useSelector(selectModalById(LCOGeneratorModalId));

  const viewportState = useSelector(selectViewportState);
  const completedSurveySetting =
    typeof user?.settings?.completedSurvey === "string" ? user.settings.completedSurvey.trim() : "";
  const shouldPromptForSurvey = completedSurveySetting.length === 0;
  const surveyPromptAt = Number(user?.settings?.surveyPromptAt ?? 0);

  useEffect(() => {
    if (!shouldPromptForSurvey || isSurveyModalOpen || !surveyPromptAt || userDidCLoseSurvey) {
      return;
    }

    const delay = surveyPromptAt - Date.now();
    if (delay <= 0) {
      dispatch(openModal("surveyModal"));
      return;
    }

    surveyModalTimerRef.current = window.setTimeout(() => {
      surveyModalTimerRef.current = null;
      dispatch(openModal("surveyModal"));
    }, delay);

    return () => {
      if (surveyModalTimerRef.current !== null) {
        window.clearTimeout(surveyModalTimerRef.current);
        surveyModalTimerRef.current = null;
      }
    };
  }, [dispatch, isSurveyModalOpen, shouldPromptForSurvey, surveyPromptAt]);

  useEffect(() => {
    surveyPromptRequestedRef.current = shouldPromptForSurvey && surveyPromptAt > 0;
  }, [shouldPromptForSurvey, surveyPromptAt]);

  const [releaseNotes, setReleaseNotes] = useState<IReleaseNotes | undefined>(undefined);
  const latestReleaseVersion = releaseNotes?.releases?.[0]?.version;

  useEffect(() => {
    const fetchReleaseNotes = async () => {
      try {
        const response = await fetch("/api/releaseNotes");
        if (!response.ok) {
          throw new Error(`Failed to fetch release notes: ${response.status}`);
        }
        setReleaseNotes((await response.json()) as IReleaseNotes);
      } catch (error) {
        console.error(error);
      }
    };
    fetchReleaseNotes();
  }, []);

  // Show what is new once per release, after the disclaimer so the two never stack. The version
  // seen is stored on the user, not in the browser, so it holds across devices.
  useEffect(() => {
    if (!latestReleaseVersion || !user?.settings || isDisclaimerModalOpen) {
      return;
    }
    if (user.settings.lastReleaseNoteShown !== latestReleaseVersion) {
      dispatch(openModal("latestReleaseNotesModal"));
    }
  }, [dispatch, latestReleaseVersion, user, isDisclaimerModalOpen]);

  // Only touch the theme attribute when it's ours, so a typed easter-egg theme isn't clobbered.
  useEffect(() => {
    const root = document.documentElement;
    if (user?.settings?.darkModeEnabled) {
      root.setAttribute("data-theme", "dark-theme");
    } else if (root.getAttribute("data-theme") === "dark-theme") {
      root.removeAttribute("data-theme");
    }
  }, [user?.settings?.darkModeEnabled]);

  // States for calculation results
  const [supportForces, setSupportForces] = useState({} as IReactionForceDictionary);
  const [userDidCLoseSurvey, setUserDidCLoseSurvey] = useState(false);

  const [elementResultsForces, setElementResultsForces] = useState({} as ISectionForceDictionary);
  const [displacement, setDisplacement] = useState({} as IDisplacementDictionary);
  // 1st order counterpart of the two dictionaries above, only populated when both the 1st and 2nd
  // order result diagrams are shown at once (see show1stOrder/show2ndOrder below), so the member
  // diagrams can overlay the 1st order curve, faded, behind the 2nd order one.
  const [elementResultsForcesSecondary, setElementResultsForcesSecondary] = useState({} as ISectionForceDictionary);
  const [displacementSecondary, setDisplacementSecondary] = useState({} as IDisplacementDictionary);
  const [bucklingMode, setBucklingMode] = useState({} as number[][]);
  const [betas, setBetas] = useState({} as number[][]);

  const [designResult, setDesignResult] = useState({} as IDesignResultDictionary);
  const [isUlsComb, setIsUlsComb] = useState(false);
  const [isSlsComb, setIsSlsComb] = useState(false);
  const [codeCheckResultExists, setCodeCheckResultExists] = useState(false);
  const [selLC, setSelLC] = useState<string>("-1");
  const [showAnalysisButton, setShowAnalysisButton] = useState(true);
  const secOrder = useAppSelector(selectSecondOrder);
  // Which order(s) the member result diagrams show. A first-order-only analysis locks these to
  // 1st-order-only (see the lock effect below); a second-order analysis defaults to 2nd order and
  // requires at least one of the two to stay on - both on overlays the 1st order curve (faded)
  // behind the 2nd order one.
  const [show1stOrder, setShow1stOrder] = useState(false);
  const [show2ndOrder, setShow2ndOrder] = useState(true);
  // Existing diagram/result-building code below already selects between the 1st and 2nd order
  // result sets via this single flag - kept as the "primary" order so none of it needs to change.
  const showSecOrder = show2ndOrder;
  // Both toggles on at once - the case that needs a second, faded curve behind the primary one.
  const showBothOrders = secOrder && show1stOrder && show2ndOrder;
  // Discreet caption shown over the viewport, reusing the toggle labels rather than introducing
  // separate copy for the same three states.
  const orderResultsLabel = showBothOrders
    ? t("1st_and_2nd_order_results_.label")
    : show2ndOrder
      ? t("show_2nd_order_results_.label")
      : t("show_1st_order_results_.label");

  // Demo state for two-way switch
  const [twoWaySwitchOn, setTwoWaySwitchOn] = useState(false);

  // Demo state for state-button group
  const [activeState, setActiveState] = useState(1);

  // Panel state
  const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(true);
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(false);
  // Active tab of the right panel ("code-check" | "design"); controlled so a
  // fresh code check can force the results into view (see didCodeCheckJustSucceed).
  const [rightPanelTab, setRightPanelTab] = useState<string>("code-check");
  const [isPrinting, setIsPrinting] = useState(false);
  const [graphPointsMode, setGraphPointsMode] = useState<GraphPointsMode>("max");
  const previousCodeCheckStatus = useRef(codeCheckStatus);
  const previousAnalysisStatus = useRef(analysisStatus);
  const previousIsFireAccordionOpen = useRef(false);
  const previousHasFireLoadCombination = useRef(false);
  const previousIsBucklingAccordionOpen = useRef(false);
  const previousProjectGuid = useRef<string | undefined>(selProject?.guid);
  const previousHasPersistedResults = useRef(hasPersistedResults);

  // Panel widths (px) — keep in sync with SCSS
  const LEFT_PANEL_WIDTH = 650;
  const COLLAPSED_PANEL_WIDTH = 40;
  const RIGHT_PANEL_WIDTH = 650;

  // --- Print handler ---
  const handlePrint = async (printTemplate: "reduced" | "normal" | "full") => {
    if (!selProject || isPrinting || printBlockedReason) return;

    // Open placeholder tab immediately to avoid popup blocking
    const placeholder = window.open("about:blank", "_blank");
    if (!placeholder) {
      alert(t("print_popup_blocked_.label", "Popup was blocked. Please allow popups and try again."));
      return;
    }
    placeholder.document.title = "Preparing PDF...";
    placeholder.document.body.innerHTML = `
      <div style="font-family: Segoe UI, Arial, sans-serif; min-height: 100vh; margin: 0; display: flex; align-items: center; justify-content: center; background: #f5f7fa; color: #1f2937;">
        <div style="text-align: center; max-width: 420px; padding: 24px;">
          <div style="width: 28px; height: 28px; border: 3px solid #cbd5e1; border-top-color: #2563eb; border-radius: 50%; margin: 0 auto 14px auto; animation: sx-spin 0.9s linear infinite;"></div>
          <h1 style="font-size: 18px; font-weight: 600; margin: 0 0 8px 0;">Preparing your PDF</h1>
          <p style="font-size: 14px; line-height: 1.4; margin: 0; color: #4b5563;">This can take a few seconds for larger files. This tab will update automatically when the document is ready.</p>
        </div>
      </div>
      <style>
        @keyframes sx-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      </style>
    `;

    setIsPrinting(true);
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 120_000);
    try {
      // Persist current state so Center reads the latest version from the DB. Unwrapped so a failed
      // save stops the print instead of printing whatever version the DB still holds.
      await dispatch(updateProject({ guid: selProject.guid, project: { data: selProject.data } })).unwrap();

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "X-Client-ID": String(getClientId()),
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const resp = await fetch("/api/print/start", {
        method: "POST",
        headers,
        body: JSON.stringify({
          fileGuid: selProject.guid,
          appType: AppType.SIMPLEX_APP_COLUMN,
          projectName: selProject.name,
          fileName: selProject.name,
          printTemplate,
        }),
        signal: controller.signal,
      });

      window.clearTimeout(timeoutId);

      if (!resp.ok) {
        throw new Error(`Print failed (${resp.status})`);
      }

      const payload = (await resp.json()) as { viewerUrl?: string };
      if (!payload?.viewerUrl) {
        throw new Error("Missing print viewer URL in response");
      }

      if (!placeholder.closed) {
        placeholder.document.title = "PDF Ready";
        placeholder.location.replace(payload.viewerUrl);
      }
    } catch (err) {
      window.clearTimeout(timeoutId);
      console.error("[print] handlePrint error:", err);
      const isTimeout = err instanceof DOMException && err.name === "AbortError";
      if (placeholder && !placeholder.closed) {
        placeholder.document.title = "PDF generation failed";
        placeholder.document.body.innerHTML = `
          <div style="font-family: Segoe UI, Arial, sans-serif; min-height: 100vh; margin: 0; display: flex; align-items: center; justify-content: center; background: #f8fafc; color: #111827;">
            <div style="text-align: center; max-width: 480px; padding: 24px;">
              <h1 style="font-size: 18px; font-weight: 600; margin: 0 0 8px 0;">Could not prepare PDF</h1>
              <p style="font-size: 14px; line-height: 1.4; margin: 0 0 14px 0; color: #4b5563;">${isTimeout ? "The request timed out while preparing your PDF. Please try again." : "An error occurred while preparing your PDF. Please close this tab and try again."}</p>
              <button type="button" onclick="window.location.href='${window.location.href}'" style="appearance: none; border: 0; border-radius: 8px; background: #2563eb; color: #fff; padding: 10px 16px; font-size: 14px; font-weight: 600; cursor: pointer;">Retry From App</button>
            </div>
          </div>
        `;
      }
    } finally {
      setIsPrinting(false);
    }
  };

  // --- Local variables ---
  const dropLCOs = useAppSelector(activeLCOs);

  const lang = "en";
  const zeroUtilisation = 0.001;
  let input_: ng.input.Data_json = selProject?.data?.data?.input ?? ({} as ng.input.Data_json);
  let output_ = selProject?.data?.data?.output || ({} as ng.output.Data_json);
  let structure_ = {} as ng.structure.Data_json;
  let struct_: ng.structure.Data_json;
  let localisation = {};
  let supportsIdsNames = [] as ng.support.Data_json[];
  let stages_ = [] as ng.stage.Data_json[];
  let stageSupports_ = [] as ng.stage.Support_json[];
  let loadcombinations_ = [] as ng.loadcombination.Data_json[];
  let loads_ = [] as ng.load.Data_json[];
  let loading_ = {} as ng.loading.Data_json;
  let element_ = {} as ng.element.Data_json;
  if (input_ && input_.structures && input_.structures[0]?.loading) {
    structure_ = input_.structures[0];

    struct_ = input_.structures[0] as ng.structure.Data_json;
    loadcombinations_ = input_.structures[0].loading.combinations || [];
    loads_ = input_.structures[0].loading?.loads || [];
    loading_ = input_.structures[0].loading;
    if (structure_.elements && structure_.elements[0]) {
      element_ = structure_.elements[0];
    }
    if (structure_ && structure_.stages && structure_.sups) {
      stages_ = structure_.stages;
      supportsIdsNames = structure_.sups;
    }
    if (stages_[0] && stages_[0].sups) {
      stageSupports_ = stages_[0].sups;
    }
  }

  let show1stOrd_noBow = false;
  if (!secondOrder && element_ && element_.beam && (element_.beam.steel || element_.beam.timber)) {
    show1stOrd_noBow = true;
  }

  const loadcombinationDropdownItems = loadcombinations_
    .filter((lc) => !!lc?.id?.guid && !lc.deactive)
    // ULS combinations first, SLS last. The sort is stable, so combinations within the same limit
    // state keep the order they were defined in.
    .sort(byLimitState)
    .map(
      (lc): NGDropdownListItem<string> => ({
        name: lc.id?.name || lc.id!.guid!,
        id: lc.id!.guid!,
      }),
    );

  const hasFireLoadCombination = loadcombinations_.some((loadCombination) => {
    const type =
      loadCombination?.type != null
        ? ng.loadcombination.Type_FromObject(loadCombination.type)
        : ng.loadcombination.Type.TYPE_UNSPECIFIED;

    return type === ng.loadcombination.Type.TYPE_FIRE;
  });
  const isFireIndicatorDisabled = !isFireAccordionOpen || !hasFireLoadCombination;

  const selectedLcoItem = loadcombinationDropdownItems.find((lc) => lc.id === selLC) ?? null;
  // Envelopes are a valid selection that is deliberately not part of the list, so they must not be
  // treated as a stale selection below.
  const isEnvelopeSelection = selLC === "envelope" || selLC.startsWith("envelope_");

  // The dropdown can only be empty for two reasons, and saying which one it is spares the user from
  // guessing: either no combinations have been created yet, or they exist but every group is
  // switched off under "Use" in the load combination input.
  const lcoEmptyKey =
    loadcombinations_.length === 0 ? "no_loadcombinations_.label" : "no_active_loadcombinations_.label";
  const lcoEmptyLabel = t(lcoEmptyKey);

  const lcoSections: Array<SXDropdownSection<string>> = [
    {
      header: t("load_combinations_.label"),
      emptyText: lcoEmptyLabel,
      items: loadcombinationDropdownItems,
      itemIdPrefix: "lco_dropdown_loadcombination",
    },
  ];

  // Never leave the dropdown in the "nothing selected" state while there is something to select.
  // This keys off the item the toggle actually renders, so every way of invalidating the selection
  // (combinations deleted, deactivated and reactivated, replaced by a regeneration) is covered.
  useEffect(() => {
    if (loadcombinationDropdownItems.length === 0 || isEnvelopeSelection || selectedLcoItem) {
      return;
    }

    setSelLC(loadcombinationDropdownItems[0].id);
  }, [selectedLcoItem, isEnvelopeSelection, loadcombinationDropdownItems]);

  useEffect(() => {
    async function fetchData() {
      if (params.fileId) {
        let a = await getProject(params.fileId);
        console.log(a);
      }
      console.log("Route params:", params);
    }

    fetchData();
  }, []);

  // Surface the (already-localised) error from the last failed calculation as an
  // alert, then clear it so the same error can be raised again on the next attempt.
  // The message is produced in runCalcThunk and stored on ProjectSlice.calcError.
  useEffect(() => {
    if (!calcError) return;
    AlertMessage("error_", t("calculation_failed_.label", "Calculation failed"), "", calcError);
    dispatch(ProjectSlice.actions.dismissCalcError());
  }, [calcError, dispatch, t]);

  /* check for mobile size screen*/
  useEffect(() => {
    const handleResize = () => {
      dispatch(setIsMobile(window.innerWidth < mobileWarningBreakpoint));
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [dispatch, mobileWarningBreakpoint]);

  // Make sure we hold the result
  useEffect(() => {
    if (!selProject?.guid) {
      previousProjectGuid.current = undefined;
      previousHasPersistedResults.current = hasPersistedResults;
      setIsRightPanelOpen(false);
      return;
    }

    const projectChanged = previousProjectGuid.current !== selProject.guid;
    if (projectChanged) {
      setIsRightPanelOpen(false);
    }

    previousProjectGuid.current = selProject.guid;
    previousHasPersistedResults.current = hasPersistedResults;
  }, [selProject?.guid, hasPersistedResults]);

  useEffect(() => {
    if (selProject === null) {
      return;
    }

    output_ = selProject?.data?.data?.output || ({} as ng.output.Data_json);

    let supportForce_ = {} as IReactionForceDictionary;
    let elementResultsForce_ = {} as ISectionForceDictionary;
    let displacements_ = {} as IDisplacementDictionary;
    let bucklingMode_ = [] as number[][];
    let betas_ = [] as number[][];
    let lcombsResults_ = output_.lcombs != null ? output_.lcombs : [];
    let supportValue_ = {} as IStageDictionary;
    stageSupports_.forEach((sup: ng.stage.Support_json) => (supportValue_[sup.guid!] = sup));
    let designResults_ = {} as IDesignResultDictionary;
    let codeCheckResultExists_ = false;

    let maxN = 0; // find the maximum axial force along the column to be used when comparing with Pcr

    for (let res of lcombsResults_) {
      let lguid = res.lcombGuid ? res.lcombGuid : "";
      supportForce_[lguid] = {};

      // ==============================  S U P P O R T   R E S U L T S  ==============================

      if (!res.supportResults) continue;

      for (let sup of res.supportResults) {
        let supportGuid = sup.supGuid ? sup.supGuid : "";

        // Guard for undefined and null values

        if (!sup || !sup.result) {
          continue;
        }

        const result = sup.result;

        if (
          show1stOrd_noBow &&
          (!result.results || !result.results.force || !result.results.force.type || !result.results.force.value)
        ) {
          continue;
        }

        if (
          showSecOrder &&
          (!sup.result.results2nd ||
            !sup.result.results2nd.force ||
            !sup.result.results2nd.force.type ||
            !sup.result.results2nd.force.value)
        ) {
          continue;
        }

        if (
          !show1stOrd_noBow &&
          !showSecOrder &&
          (!result.resultsBowImp ||
            !result.resultsBowImp.force ||
            !result.resultsBowImp.force.type ||
            !result.resultsBowImp.force.value)
        ) {
          continue;
        }

        // Get force data
        const forceData = showSecOrder
          ? sup.result.results2nd?.force
          : show1stOrd_noBow
            ? sup.result.results?.force
            : sup.result.resultsBowImp?.force;

        if (!forceData || !forceData.type || !forceData.value) {
          continue;
        }

        // Create reaction force object
        let R = {
          pos: 0.0,
          Rx: {
            max: 0.0,
            min: 0.0,
          },
          Ry: {
            max: 0.0,
            min: 0.0,
          },
          Rz: {
            max: 0.0,
            min: 0.0,
          },
          Mx: {
            max: 0.0,
            min: 0.0,
          },
          My: {
            max: 0.0,
            min: 0.0,
          },
          Mz: {
            max: 0.0,
            min: 0.0,
          },
        };

        for (let i = 0; i < forceData.type.length; i++) {
          let forceValue = ng.result.ForceData.fromObject(forceData);

          switch (forceValue.type[i]) {
            case ng.result.Force.FORCE_RX:
              R.Rx.max = forceValue.value[i];
              break;
            case ng.result.Force.FORCE_RY:
              R.Ry.max = forceValue.value[i];
              break;
            case ng.result.Force.FORCE_RZ:
              R.Rz.max = forceValue.value[i];
              break;
            case ng.result.Force.FORCE_MX:
              R.Mx.max = forceValue.value[i];
              break;
            case ng.result.Force.FORCE_MY:
              R.My.max = forceValue.value[i];
              break;
            case ng.result.Force.FORCE_MZ:
              R.Mz.max = forceValue.value[i];
              break;
            default:
              break;
          }
        }

        // Get support coordinates
        const supportValue_SupportGuid = ng.stage.Support.fromObject(supportValue_[supportGuid]);

        if (!supportValue_ || !supportValue_SupportGuid || supportValue_SupportGuid.position?.type !== "coord") {
          continue;
        }

        // Get coordinate
        let coord = supportValue_SupportGuid.position.coord;
        if (!coord) {
          continue;
        }

        R.pos = coord.x ? coord.x : 0;

        // Set force object
        if (!supportForce_[lguid].hasOwnProperty(supportGuid)) supportForce_[lguid][supportGuid] = [];
        supportForce_[lguid][supportGuid].push(R);
      }

      // ===========================================================================================

      // Initialize element results
      displacements_[lguid] = {};
      elementResultsForce_[lguid] = {};
      designResults_[lguid] = {};

      // Guard for element results
      if (!res.elementResults) continue;

      let resObj = ng.output.LoadCombinationOutput.fromObject(res);
      let elResults = resObj.elementResults.map((x: any) => x.deepClone());

      // Now loop element results and assemble displacement and force results
      for (let elRes of elResults) {
        // Guard for undefined and null values
        if (!elRes.result || !elRes.result.positions) continue;

        let res = elRes.result.deepClone();

        let positions = res.positions;
        let currentElguid = elRes.elemGuid ? elRes.elemGuid : "";

        // Get material type for design results
        let material = ng.material.Mtrl.MTRL_UNSPECIFIED;
        switch (positions[0].design?.type) {
          case "rc":
            material = ng.material.Mtrl.MTRL_CONCRETE;
            break;
          case "steel":
            material = ng.material.Mtrl.MTRL_STEEL;
            break;
          case "timber":
            material = ng.material.Mtrl.MTRL_TIMBER;
            break;
        }

        //------------------------------------------------------------------------------------------------

        // Collect element forces and displacements from the ORIGINAL analysis positions,
        // before any shear-field pre-processing mutates force values or splices extra positions.
        for (let pos of positions) {
          const posDistance = pos.getDistance();
          if (posDistance == null || posDistance === undefined) {
            continue;
          }

          // ======================   D I S P L A C E M E N T S   ======================

          // Guard for undefined and null values
          if (
            show1stOrd_noBow &&
            (!pos || !pos.results || !pos.results.displacement || !pos.results.displacement.value)
          ) {
            continue;
          }
          if (
            showSecOrder &&
            (!pos || !pos.results2nd || !pos.results2nd.displacement || !pos.results2nd.displacement.value)
          ) {
            continue;
          }
          if (
            !show1stOrd_noBow &&
            !showSecOrder &&
            (!pos || !pos.resultsBowImp || !pos.resultsBowImp.displacement || !pos.resultsBowImp.displacement.value)
          ) {
            continue;
          }

          // Get displacement data
          const dispData = showSecOrder
            ? pos.results2nd?.displacement
            : show1stOrd_noBow
              ? pos.results?.displacement
              : pos.resultsBowImp?.displacement;

          if (!dispData || !dispData.type || !dispData.value) {
            continue;
          }

          // Create displacement object
          let dis: IDisplacement = {
            pos: posDistance,
            u: {
              max: 0.0,
              min: 0.0,
            },
            v: {
              max: 0.0,
              min: 0.0,
            },
            w: {
              max: 0.0,
              min: 0.0,
            },
            x: {
              max: 0.0,
              min: 0.0,
            },
            y: {
              max: 0.0,
              min: 0.0,
            },
            z: {
              max: 0.0,
              min: 0.0,
            },
          };

          for (let i = 0; i < dispData.type.length; i++) {
            switch (dispData.type[i]) {
              case ng.result.Displacement.DISPLACEMENT_U:
                dis.u.max = dispData.value[i];
                break;
              case ng.result.Displacement.DISPLACEMENT_V:
                dis.v.max = dispData.value[i];
                break;
              case ng.result.Displacement.DISPLACEMENT_W:
                dis.w.max = dispData.value[i];
                break;
              case ng.result.Displacement.DISPLACEMENT_X:
                dis.x.max = dispData.value[i];
                break;
              case ng.result.Displacement.DISPLACEMENT_Y:
                dis.y.max = dispData.value[i];
                break;
              case ng.result.Displacement.DISPLACEMENT_Z:
                dis.z.max = dispData.value[i];
                break;
              default:
                break;
            }
          }

          // ======================   E L E M E N T  R E S U L T S  ======================

          // Guard for undefined and null values

          if (show1stOrd_noBow && (!pos || !pos.results || !pos.results.force || !pos.results.force.value)) {
            continue;
          }
          if (showSecOrder && (!pos || !pos.results2nd || !pos.results2nd.force || !pos.results2nd.force.value)) {
            continue;
          }
          if (
            !show1stOrd_noBow &&
            !showSecOrder &&
            (!pos || !pos.resultsBowImp || !pos.resultsBowImp.force || !pos.resultsBowImp.force.value)
          ) {
            continue;
          }

          // Get force data
          const forceData = showSecOrder
            ? pos.results2nd?.force
            : show1stOrd_noBow
              ? pos.results?.force
              : pos.resultsBowImp?.force;
          if (!forceData || !forceData.type || !forceData.value) {
            continue;
          }

          // Create force object
          let F: ISectionForce = {
            pos: posDistance,
            el: currentElguid,
            N: {
              max: 0.0,
              min: 0.0,
            },
            V1: {
              max: 0.0,
              min: 0.0,
            },
            V2: {
              max: 0.0,
              min: 0.0,
            },
            T: {
              max: 0.0,
              min: 0.0,
            },
            M1: {
              max: 0.0,
              min: 0.0,
            },
            M2: {
              max: 0.0,
              min: 0.0,
            },
          };

          for (let i = 0; i < forceData.type.length; i++) {
            switch (forceData.type[i]) {
              case ng.result.Force.FORCE_N:
                F.N.max = forceData.value[i];
                if (Math.abs(forceData.value[i]) > Math.abs(maxN)) {
                  maxN = forceData.value[i];
                }
                break;
              case ng.result.Force.FORCE_V1:
                F.V1.max = forceData.value[i];
                break;
              case ng.result.Force.FORCE_V2:
                F.V2.max = forceData.value[i];
                break;
              case ng.result.Force.FORCE_T:
                F.T.max = forceData.value[i];
                break;
              case ng.result.Force.FORCE_M1:
                F.M1.max = forceData.value[i];
                break;
              case ng.result.Force.FORCE_M2:
                F.M2.max = forceData.value[i];
                break;
              default:
                break;
            }
          }

          //----------------------------------------------------------------------------------------

          // Set displacement and force object
          if (!displacements_[lguid].hasOwnProperty(currentElguid)) displacements_[lguid][currentElguid] = [];
          displacements_[lguid][currentElguid].push(structuredClone(dis));

          if (!elementResultsForce_[lguid].hasOwnProperty(currentElguid))
            elementResultsForce_[lguid][currentElguid] = [];
          elementResultsForce_[lguid][currentElguid].push(structuredClone(F));
        }

        //------------------------------------------------------------------------------------------------

        // Pre-processing: shear-field force substitution for section force diagrams.
        // This mutates positions in-place (and splices extra boundary positions) so that the
        // V1 section force diagram shows shearFieldForce. The collected elementResultsForce_
        // above is NOT affected — it contains the original analysis values.
        let poss = positions;
        let fieldEnd = Number.MAX_VALUE;
        let fieldStart = 0;
        let hasCodeCheckResult = false;
        for (let i = 0; i < poss.length; i++) {
          let pos = poss[i];

          const dist = pos.getDistance() ?? 0;

          let rc = pos.getRc();
          if (rc === null) continue;
          let prevRc = rc;

          if (rc && selLC !== "envelope") {
            //concrete
            if (rc.bendSls) {
              const dispVals = showSecOrder
                ? pos.results2nd?.displacement
                : show1stOrd_noBow
                  ? pos.results?.displacement
                  : pos.resultsBowImp?.displacement;

              if (dispVals) {
                setDisplacementValueForType(
                  pos,
                  showSecOrder,
                  show1stOrd_noBow,
                  ng.result.Displacement.DISPLACEMENT_V,
                  rc.bendSls.crkStageBtm === 2 ? rc.bendSls.crackWidth : -rc.bendSls.crackWidth,
                );
                hasCodeCheckResult = true;
              }
            }
            if (rc.stirrup) {
              const forceVals = showSecOrder
                ? pos.results2nd?.force
                : show1stOrd_noBow
                  ? pos.results?.force
                  : pos.resultsBowImp?.force;

              if (forceVals) {
                if (dist >= fieldEnd) {
                  // NOTE! we have to add two extra points for the 'step' at the end of the shear field
                  let lft = pos.deepClone();
                  lft.setDistance(fieldEnd);

                  const prevPos = poss[i - 1];
                  if (prevPos.design?.type === "rc") {
                    prevRc = prevPos.design.rc;
                  }

                  // make sure that we get the shear field info from the right field
                  if (dist === fieldEnd) {
                    if (i + 1 < poss.length) {
                      const nextPos = poss[i + 1];
                      if (nextPos.design?.type === "rc") {
                        rc = nextPos.design.rc;
                      }
                    }
                  }

                  interpolateActiveResults(pos, prevPos, dist, fieldEnd, lft, showSecOrder, show1stOrd_noBow);

                  const leftVals = showSecOrder ? lft.results2nd : show1stOrd_noBow ? lft.results : lft.resultsBowImp;
                  if (leftVals) {
                    if (leftVals.force && prevRc?.stirrup) {
                      setForceValueForType(
                        lft,
                        showSecOrder,
                        show1stOrd_noBow,
                        ng.result.Force.FORCE_V1,
                        prevRc.stirrup.shearForceStirrups > 0
                          ? prevRc.stirrup.shearFieldForce
                          : -prevRc.stirrup.shearFieldForce,
                      );
                    }
                  }

                  res.positions.splice(i++, 0, lft); //set the values at the left of breaking point

                  let rgt = lft.deepClone();

                  // update the shear field to the right of the breaking point
                  const rightVals = showSecOrder ? rgt.results2nd : show1stOrd_noBow ? rgt.results : rgt.resultsBowImp;
                  if (rightVals) {
                    if (rightVals.force && rc?.stirrup) {
                      setForceValueForType(
                        rgt,
                        showSecOrder,
                        show1stOrd_noBow,
                        ng.result.Force.FORCE_V1,
                        rc.stirrup.shearForceStirrups > 0 ? rc.stirrup.shearFieldForce : -rc.stirrup.shearFieldForce,
                      );
                    }
                  }

                  res.positions.splice(i++, 0, rgt); //set the values at the right of breaking point
                }

                if (rc.stirrup) {
                  setForceValueForType(
                    pos,
                    showSecOrder,
                    show1stOrd_noBow,
                    ng.result.Force.FORCE_V1,
                    rc.stirrup.shearForceStirrups > 0 ? rc.stirrup.shearFieldForce : -rc.stirrup.shearFieldForce,
                  );
                  if (rc.stirrup.shearFieldEnd !== fieldEnd) {
                    fieldStart = rc.stirrup.shearFieldStart;
                    fieldEnd = rc.stirrup.shearFieldEnd;
                  }
                  hasCodeCheckResult = true;
                }
              }
            }
          }
        }

        if (hasCodeCheckResult) {
          codeCheckResultExists_ = true;
        }

        // Reset 'max of controls'
        if (!designResults_[lguid].hasOwnProperty(currentElguid)) {
          designResults_[lguid][currentElguid] = [];
        }

        // Gather 'max of controls' to design results
        if (elRes.result.result) {
          // Max of concrete controls for this load combination
          if (
            elRes.result.result.design?.type === "rc" &&
            elRes.result.result.design.rc.maxOfControls &&
            elRes.result.result.design.rc.maxOfControls.length > 0
          ) {
            for (let ctrlData of elRes.result.result.design.rc.maxOfControls) {
              // The > 0 filter hides controls that were reported but carry no result (anchorage at 0).
              // The biaxial moment control is the exception: RC-Design reports it at 0 % when
              // EN 1992-1-1 5.8.9 (3) screens the check out, and that "not required" verdict is the
              // information — dropping the row would read as "never calculated" again.
              const isBiaxialMoment =
                ctrlData.type?.type === "concreteType" &&
                ctrlData.type.concreteType ===
                  ng.result.control.ControlTypeConcrete.CONTROL_TYPE_CONCRETE_BIAXIAL_MOMENT;
              if ((ctrlData.utilization > 0.0 || isBiaxialMoment) && ctrlData.type?.type === "concreteType") {
                let ct: IDesignResult = {
                  check: ctrlData.type.concreteType,
                  utilisation: ctrlData.utilization,
                  pos: ctrlData.x,
                  lco: ctrlData.lcName,
                  // The control's own formula values (see IDesignResult.values). Carried along as a
                  // plain object so the result survives structuredClone into React state; a moment
                  // control's "MEd" is what the interaction diagram plots, since it is the moment
                  // the capacity was actually checked against - second-order addition included.
                  values: ctrlData.values ? Object.fromEntries(ctrlData.values) : undefined,
                };
                designResults_[lguid][currentElguid].push(ct);
              }
            }
          }

          // Max of steel controls for this load combination
          if (
            elRes.result.result.design?.type === "steel" &&
            elRes.result.result.design.steel.maxOfControls &&
            elRes.result.result.design.steel.maxOfControls.length > 0
          ) {
            for (let ctrlData of elRes.result.result.design.steel.maxOfControls) {
              if (ctrlData.utilization > 0.0 && ctrlData.type?.type === "steelType") {
                let ct: IDesignResult = {
                  check: ctrlData.type.steelType,
                  utilisation: ctrlData.utilization,
                  pos: ctrlData.x,
                  lco: ctrlData.lcName,
                };
                designResults_[lguid][currentElguid].push(ct);
              }
            }
          }

          // Max of timber controls for this load combination
          if (
            elRes.result.result.design?.type === "timber" &&
            elRes.result.result.design.timber.maxOfControls &&
            elRes.result.result.design.timber.maxOfControls.length > 0
          ) {
            for (let ctrlData of elRes.result.result.design.timber.maxOfControls) {
              if (ctrlData.utilization > 0.0 && ctrlData.type?.type === "timberType") {
                let ct: IDesignResult = {
                  check: ctrlData.type.timberType,
                  utilisation: ctrlData.utilization,
                  pos: ctrlData.x,
                  lco: ctrlData.lcName,
                };
                designResults_[lguid][currentElguid].push(ct);
              }
            }
          }
        }
      }
    }

    if (selLC === "envelope") {
      elementResultsForce_["envelope"] = {};
      displacements_["envelope"] = {};
      supportForce_["envelope"] = {};
      designResults_["envelope"] = {};
      let selectedLCOs = [] as string[];
      //get first load combination guid in order to get the elements
      let lcombGuid = "<no selected lco>";

      //--------------------------------- F O R C E S ---------------------------------
      //--------------------------------- D I S P L A C E M E N T S ---------------------------------
      if (elementResultsForce_ && elementResultsForce_[lcombGuid]) {
        Object.keys(elementResultsForce_[lcombGuid]).forEach((elGuid: string) => {
          elementResultsForce_["envelope"][elGuid] = [];

          for (let lcoGuid of selectedLCOs) {
            if (elementResultsForce_[lcoGuid] === undefined) {
              continue;
            }

            if (elementResultsForce_[lcoGuid][elGuid] === undefined) {
              continue;
            }

            let tmp_f = elementResultsForce_[lcoGuid][elGuid];

            if (elementResultsForce_["envelope"][elGuid].length > 0) {
              for (let i = 0; i < tmp_f.length; i++) {
                //N
                if (tmp_f[i].N.max > elementResultsForce_["envelope"][elGuid][i].N.max) {
                  elementResultsForce_["envelope"][elGuid][i].N.max = tmp_f[i].N.max;
                }
                if (tmp_f[i].N.max < elementResultsForce_["envelope"][elGuid][i].N.min) {
                  //NOTE! yes is should be .max!
                  elementResultsForce_["envelope"][elGuid][i].N.min = tmp_f[i].N.max;
                }

                // V1
                if (tmp_f[i].V1.max > elementResultsForce_["envelope"][elGuid][i].V1.max) {
                  elementResultsForce_["envelope"][elGuid][i].V1.max = tmp_f[i].V1.max;
                }
                if (tmp_f[i].V1.max < elementResultsForce_["envelope"][elGuid][i].V1.min) {
                  //NOTE! yes is should be .max!
                  elementResultsForce_["envelope"][elGuid][i].V1.min = tmp_f[i].V1.max;
                }

                // V2
                if (tmp_f[i].V2.max > elementResultsForce_["envelope"][elGuid][i].V2.max) {
                  elementResultsForce_["envelope"][elGuid][i].V2.max = tmp_f[i].V2.max;
                }
                if (tmp_f[i].V2.max < elementResultsForce_["envelope"][elGuid][i].V2.min) {
                  //NOTE! yes is should be .max!
                  elementResultsForce_["envelope"][elGuid][i].V2.min = tmp_f[i].V2.max;
                }

                // T
                if (tmp_f[i].T.max > elementResultsForce_["envelope"][elGuid][i].T.max) {
                  elementResultsForce_["envelope"][elGuid][i].T.max = tmp_f[i].T.max;
                }
                if (tmp_f[i].T.max < elementResultsForce_["envelope"][elGuid][i].T.min) {
                  //NOTE! yes is should be .max!
                  elementResultsForce_["envelope"][elGuid][i].T.min = tmp_f[i].T.max;
                }

                // M1
                if (tmp_f[i].M1.max > elementResultsForce_["envelope"][elGuid][i].M1.max) {
                  elementResultsForce_["envelope"][elGuid][i].M1.max = tmp_f[i].M1.max;
                }
                if (tmp_f[i].M1.max < elementResultsForce_["envelope"][elGuid][i].M1.min) {
                  //NOTE! yes is should be .max!
                  elementResultsForce_["envelope"][elGuid][i].M1.min = tmp_f[i].M1.max;
                }

                //M2
                if (tmp_f[i].M2.max > elementResultsForce_["envelope"][elGuid][i].M2.max) {
                  elementResultsForce_["envelope"][elGuid][i].M2.max = tmp_f[i].M2.max;
                }
                if (tmp_f[i].M2.max < elementResultsForce_["envelope"][elGuid][i].M2.min) {
                  //NOTE! yes is should be .max!
                  elementResultsForce_["envelope"][elGuid][i].M2.min = tmp_f[i].M2.max;
                }
              }
            } else {
              for (let i = 0; i < tmp_f.length; i++) {
                let tmpF = {
                  pos: tmp_f[i].pos,
                  el: tmp_f[i].el,
                  N: {
                    max: tmp_f[i].N.max > 0.0 ? tmp_f[i].N.max : 0.0,
                    min: tmp_f[i].N.max < 0.0 ? tmp_f[i].N.max : 0.0,
                  },
                  V1: {
                    max: tmp_f[i].V1.max > 0.0 ? tmp_f[i].V1.max : 0.0,
                    min: tmp_f[i].V1.max < 0.0 ? tmp_f[i].V1.max : 0.0,
                  },
                  V2: {
                    max: tmp_f[i].V2.max > 0.0 ? tmp_f[i].V2.max : 0.0,
                    min: tmp_f[i].V2.max < 0.0 ? tmp_f[i].V2.max : 0.0,
                  },
                  T: {
                    max: tmp_f[i].T.max > 0.0 ? tmp_f[i].T.max : 0.0,
                    min: tmp_f[i].T.max < 0.0 ? tmp_f[i].T.max : 0.0,
                  },
                  M1: {
                    max: tmp_f[i].M1.max > 0.0 ? tmp_f[i].M1.max : 0.0,
                    min: tmp_f[i].M1.max < 0.0 ? tmp_f[i].M1.max : 0.0,
                  },
                  M2: {
                    max: tmp_f[i].M2.max > 0.0 ? tmp_f[i].M2.max : 0.0,
                    min: tmp_f[i].M2.max < 0.0 ? tmp_f[i].M2.max : 0.0,
                  },
                };

                elementResultsForce_["envelope"][elGuid].push(tmpF);
              }
            }
          }

          displacements_["envelope"][elGuid] = [];

          for (let lcoGuid of selectedLCOs!) {
            let tmp_d = displacements_[lcoGuid][elGuid];

            if (displacements_["envelope"][elGuid].length > 0) {
              for (let i = 0; i < tmp_d.length; i++) {
                //u
                if (tmp_d[i].u.max > displacements_["envelope"][elGuid][i].u.max) {
                  displacements_["envelope"][elGuid][i].u.max = tmp_d[i].u.max;
                }
                if (tmp_d[i].u.max < displacements_["envelope"][elGuid][i].u.min) {
                  //NOTE! yes is should be .max!
                  displacements_["envelope"][elGuid][i].u.min = tmp_d[i].u.max;
                }

                // v
                if (tmp_d[i].v.max > displacements_["envelope"][elGuid][i].v.max) {
                  displacements_["envelope"][elGuid][i].v.max = tmp_d[i].v.max;
                }
                if (tmp_d[i].v.max < displacements_["envelope"][elGuid][i].v.min) {
                  //NOTE! yes is should be .max!
                  displacements_["envelope"][elGuid][i].v.min = tmp_d[i].v.max;
                }

                // w
                if (tmp_d[i].w.max > displacements_["envelope"][elGuid][i].w.max) {
                  displacements_["envelope"][elGuid][i].w.max = tmp_d[i].w.max;
                }
                if (tmp_d[i].w.max < displacements_["envelope"][elGuid][i].w.min) {
                  //NOTE! yes is should be .max!
                  displacements_["envelope"][elGuid][i].w.min = tmp_d[i].w.max;
                }
              }
            } else {
              for (let i = 0; i < tmp_d.length; i++) {
                let tmpD = {
                  pos: tmp_d[i].pos,
                  u: {
                    max: tmp_d[i].u.max > 0.0 ? tmp_d[i].u.max : 0.0,
                    min: tmp_d[i].u.max < 0.0 ? tmp_d[i].u.max : 0.0,
                  },
                  v: {
                    max: tmp_d[i].v.max > 0.0 ? tmp_d[i].v.max : 0.0,
                    min: tmp_d[i].v.max < 0.0 ? tmp_d[i].v.max : 0.0,
                  },
                  w: {
                    max: tmp_d[i].w.max > 0.0 ? tmp_d[i].w.max : 0.0,
                    min: tmp_d[i].w.max < 0.0 ? tmp_d[i].w.max : 0.0,
                  },
                  x: {
                    max: tmp_d[i].x.max > 0.0 ? tmp_d[i].x.max : 0.0,
                    min: tmp_d[i].x.max < 0.0 ? tmp_d[i].x.max : 0.0,
                  },
                  y: {
                    max: tmp_d[i].y.max > 0.0 ? tmp_d[i].y.max : 0.0,
                    min: tmp_d[i].y.max < 0.0 ? tmp_d[i].y.max : 0.0,
                  },
                  z: {
                    max: tmp_d[i].z.max > 0.0 ? tmp_d[i].z.max : 0.0,
                    min: tmp_d[i].z.max < 0.0 ? tmp_d[i].z.max : 0.0,
                  },
                };

                displacements_["envelope"][elGuid].push(tmpD);
              }
            }
          }
        });
      }

      //--------------------------------- S U P P O R T  F O R C E S ---------------------------------

      if (supportForce_ && supportForce_[lcombGuid]) {
        Object.keys(supportForce_[lcombGuid]).forEach((supportGuid: string) => {
          supportForce_["envelope"][supportGuid] = [];

          for (let lcoGuid of selectedLCOs!) {
            let tmp_r = supportForce_[lcoGuid][supportGuid];

            if (supportForce_["envelope"][supportGuid].length > 0) {
              for (let i = 0; i < tmp_r.length; i++) {
                // V
                if (tmp_r[i].Rz.max > supportForce_["envelope"][supportGuid][i].Rz.max) {
                  supportForce_["envelope"][supportGuid][i].Rz.max = tmp_r[i].Rz.max;
                }
                if (tmp_r[i].Rz.max < supportForce_["envelope"][supportGuid][i].Rz.min) {
                  //NOTE! yes is should be .max!
                  supportForce_["envelope"][supportGuid][i].Rz.min = tmp_r[i].Rz.max;
                }
                // H
                if (tmp_r[i].Rx.max > supportForce_["envelope"][supportGuid][i].Rx.max) {
                  supportForce_["envelope"][supportGuid][i].Rx.max = tmp_r[i].Rx.max;
                }
                if (tmp_r[i].Rx.max < supportForce_["envelope"][supportGuid][i].Rx.min) {
                  //NOTE! yes is should be .max!
                  supportForce_["envelope"][supportGuid][i].Rx.min = tmp_r[i].Rx.max;
                }
                // M
                if (tmp_r[i].My.max > supportForce_["envelope"][supportGuid][i].My.max) {
                  supportForce_["envelope"][supportGuid][i].My.max = tmp_r[i].My.max;
                }
                if (tmp_r[i].My.max < supportForce_["envelope"][supportGuid][i].My.min) {
                  //NOTE! yes is should be .max!
                  supportForce_["envelope"][supportGuid][i].My.min = tmp_r[i].My.max;
                }
                // T
                if (tmp_r[i].Mx.max > supportForce_["envelope"][supportGuid][i].Mx.max) {
                  supportForce_["envelope"][supportGuid][i].Mx.max = tmp_r[i].Mx.max;
                }
                if (tmp_r[i].Mx.max < supportForce_["envelope"][supportGuid][i].Mx.min) {
                  //NOTE! yes is should be .max!
                  supportForce_["envelope"][supportGuid][i].Mx.min = tmp_r[i].Mx.max;
                }
              }
            } else {
              for (let i = 0; i < tmp_r.length; i++) {
                let tmpR = {
                  pos: tmp_r[i].pos,
                  Rx: {
                    max: tmp_r[i].Rx.max > 0.0 ? tmp_r[i].Rx.max : 0.0,
                    min: tmp_r[i].Rx.max < 0.0 ? tmp_r[i].Rx.max : 0.0,
                  },
                  Ry: {
                    max: 0.0,
                    min: 0.0,
                  },
                  Rz: {
                    max: tmp_r[i].Rz.max > 0.0 ? tmp_r[i].Rz.max : 0.0,
                    min: tmp_r[i].Rz.max < 0.0 ? tmp_r[i].Rz.max : 0.0,
                  },
                  Mx: {
                    max: tmp_r[i].Mx.max > 0.0 ? tmp_r[i].Mx.max : 0.0,
                    min: tmp_r[i].Mx.max < 0.0 ? tmp_r[i].Mx.max : 0.0,
                  },
                  My: {
                    max: tmp_r[i].My.max > 0.0 ? tmp_r[i].My.max : 0.0,
                    min: tmp_r[i].My.max < 0.0 ? tmp_r[i].My.max : 0.0,
                  },
                  Mz: {
                    max: 0.0,
                    min: 0.0,
                  },
                };

                supportForce_["envelope"][supportGuid].push(tmpR);
              }
            }
          }
        });
      }

      //--------------------------------- D E S I G N ---------------------------------

      if (designResults_ && designResults_[lcombGuid]) {
        Object.keys(designResults_[lcombGuid]).forEach((elGuid: string) => {
          let findRes = true;
          for (let lcoGuid of selectedLCOs!) {
            if (designResults_[lcoGuid][elGuid] === undefined || designResults_[lcoGuid][elGuid].length === 0) {
              findRes = false;
              break;
            }
          }

          if (findRes) {
            // Begin with the first load combination in the envelope
            designResults_["envelope"][elGuid] = structuredClone(designResults_[selectedLCOs![0]][elGuid]);

            // Loop through the rest of the load combinations
            for (let lcoGuid of selectedLCOs!) {
              // Get the design results for the current load combination
              let tmp_des = designResults_[lcoGuid][elGuid];

              // Loop through the code checks to find the maximum utilisation
              for (let i = 0; i < tmp_des.length; i++) {
                // Get code check
                var codeCheck = tmp_des[i];

                // Make sure it is valid
                if (!codeCheck || !codeCheck.utilisation || !codeCheck.lco) {
                  continue;
                }

                // Try to find the same check in the envelope
                var match = designResults_["envelope"][elGuid].find(
                  (el: IDesignResult) => el.check === codeCheck.check,
                );

                // If found, compare utilisation and update if bigger
                if (match) {
                  if (codeCheck.utilisation > match.utilisation) {
                    match.utilisation = codeCheck.utilisation;
                    match.lco = codeCheck.lco;
                    // The formula values belong to the utilisation they were computed with - keeping
                    // the previous combination's would have the envelope quote one combination's
                    // utilisation next to another's forces.
                    match.values = codeCheck.values;
                  }
                }
                // If not found, add to the envelope
                else {
                  designResults_["envelope"][elGuid].push(codeCheck);
                }
              }
            }
          }
        });
      }
    }

    // Overlay (both 1st and 2nd order shown at once): a lighter-weight, independent extraction of
    // the 1st order (resultsBowImp) member forces/displacements, read straight from the analysis
    // output rather than reusing the loop above. That loop mutates positions in place for the RC
    // shear-field substitution and crack-width readout, all keyed to whichever order is "primary"
    // (showSecOrder) - the overlay curve is a faded reference only, with no labels (see DrawGraph),
    // so it does not need any of that, just the raw N/V/M/displacement values.
    // Only built for a single selected load combination - there is no matching 1st order envelope
    // to overlay, so the envelope selection simply shows no overlay.
    let elementResultsForceSecondary_ = {} as ISectionForceDictionary;
    let displacementsSecondary_ = {} as IDisplacementDictionary;

    if (showBothOrders && selLC !== "-1" && !isEnvelopeSelection) {
      for (let res of lcombsResults_) {
        const lguid = res.lcombGuid ? res.lcombGuid : "";
        if (!res.elementResults) continue;

        elementResultsForceSecondary_[lguid] = {};
        displacementsSecondary_[lguid] = {};

        // Mirrors the conversion used by the primary loop above: the raw lcomb output is JSON, so
        // it is turned into protobuf class instances (numeric enums, `.getDistance()`, `.deepClone()`)
        // before positions can be read the same way.
        const secResObj = ng.output.LoadCombinationOutput.fromObject(res);
        const secElResults = secResObj.elementResults.map((x: any) => x.deepClone());

        for (let elRes of secElResults) {
          if (!elRes.result || !elRes.result.positions) continue;
          const currentElguid = elRes.elemGuid ? elRes.elemGuid : "";

          for (let pos of elRes.result.positions) {
            const posDistance = pos.getDistance();
            if (posDistance == null || posDistance === undefined) continue;

            const dispData = pos.resultsBowImp?.displacement;
            const forceData = pos.resultsBowImp?.force;
            if (!dispData?.type || !dispData.value || !forceData?.type || !forceData.value) continue;

            const dis: IDisplacement = {
              pos: posDistance,
              u: { max: 0.0, min: 0.0 },
              v: { max: 0.0, min: 0.0 },
              w: { max: 0.0, min: 0.0 },
              x: { max: 0.0, min: 0.0 },
              y: { max: 0.0, min: 0.0 },
              z: { max: 0.0, min: 0.0 },
            };
            for (let i = 0; i < dispData.type.length; i++) {
              switch (dispData.type[i]) {
                case ng.result.Displacement.DISPLACEMENT_U:
                  dis.u.max = dispData.value[i];
                  break;
                case ng.result.Displacement.DISPLACEMENT_V:
                  dis.v.max = dispData.value[i];
                  break;
                case ng.result.Displacement.DISPLACEMENT_W:
                  dis.w.max = dispData.value[i];
                  break;
                case ng.result.Displacement.DISPLACEMENT_X:
                  dis.x.max = dispData.value[i];
                  break;
                case ng.result.Displacement.DISPLACEMENT_Y:
                  dis.y.max = dispData.value[i];
                  break;
                case ng.result.Displacement.DISPLACEMENT_Z:
                  dis.z.max = dispData.value[i];
                  break;
                default:
                  break;
              }
            }

            const F: ISectionForce = {
              pos: posDistance,
              el: currentElguid,
              N: { max: 0.0, min: 0.0 },
              V1: { max: 0.0, min: 0.0 },
              V2: { max: 0.0, min: 0.0 },
              T: { max: 0.0, min: 0.0 },
              M1: { max: 0.0, min: 0.0 },
              M2: { max: 0.0, min: 0.0 },
            };
            for (let i = 0; i < forceData.type.length; i++) {
              switch (forceData.type[i]) {
                case ng.result.Force.FORCE_N:
                  F.N.max = forceData.value[i];
                  break;
                case ng.result.Force.FORCE_V1:
                  F.V1.max = forceData.value[i];
                  break;
                case ng.result.Force.FORCE_V2:
                  F.V2.max = forceData.value[i];
                  break;
                case ng.result.Force.FORCE_T:
                  F.T.max = forceData.value[i];
                  break;
                case ng.result.Force.FORCE_M1:
                  F.M1.max = forceData.value[i];
                  break;
                case ng.result.Force.FORCE_M2:
                  F.M2.max = forceData.value[i];
                  break;
                default:
                  break;
              }
            }

            if (!displacementsSecondary_[lguid][currentElguid]) displacementsSecondary_[lguid][currentElguid] = [];
            displacementsSecondary_[lguid][currentElguid].push(dis);
            if (!elementResultsForceSecondary_[lguid][currentElguid])
              elementResultsForceSecondary_[lguid][currentElguid] = [];
            elementResultsForceSecondary_[lguid][currentElguid].push(F);
          }
        }
      }
    }

    let Pcr = 0;

    if (struct_ && struct_.elements && struct_.elements.length > 0) {
      let elem_ = struct_.elements[0];
      let Pcr_weak = 0;
      let Pcr_stiff = 0;

      if (elem_ && elem_.beam) {
        const beam = elem_?.beam;
        const GE = beam?.concrete ?? beam?.steel ?? beam?.timber;
        bucklingMode_[0] = GE?.fbWeak?.bucklingRes?.modes?.[0]?.v.map(Number) ?? [];
        bucklingMode_[1] = GE?.fbStiff?.bucklingRes?.modes?.[0]?.v.map(Number) ?? [];
        betas_[0] = GE?.fbWeak?.betas?.map(Number) ?? [];
        betas_[1] = GE?.fbStiff?.betas?.map(Number) ?? [];

        // --- Critical (Euler) buckling loads, computed here instead of taken
        //     from the previous analysis (GE.fb*.bucklingRes.bucklingLoad). ---
        // Pcr = pi^2 * E * I / (beta * L)^2, one value per buckling direction.
        // Everything is read from the input model in SI base units (E [Pa],
        // I [m^4], L [m]), so Pcr comes out in [N] like the old server value.

        // Member length L and effective-length factors are DENOMINATORS, so they
        // are NOT defaulted to 0 (that would divide by zero). They are parsed as-is
        // and validated by isPositive() below; an invalid one skips that direction.
        const L = Number(beam?.segments?.[0]?.length);

        // Young's modulus E: material referenced by beam.mtrlGuid in the material db.
        const mat = input_.mtrlDb?.find((m) => m.id?.guid && m.id.guid === beam?.mtrlGuid);
        const E =
          Number(
            mat?.conData?.properties?.elasticityModulus ?? // concrete
              mat?.steelData?.properties?.elasticityModulus ?? // steel
              mat?.timberData?.solidData?.meanElasticityParallel ?? // solid timber (E0,mean)
              mat?.timberData?.boardData?.elasticityBendingParallel, // glulam / board timber
          ) || 0;

        // Second moments of area: section referenced by the segment's start section guid.
        const secGuid = beam?.segments?.[0]?.layer?.start?.secGuid;
        const sec = input_.secDb?.find((s) => s.id?.guid && s.id.guid === secGuid);
        // Prefer geometric-axis inertias (iX/iY); fall back to principal inertias (i1/i2).
        const inertiaA = Number(sec?.units?.iX) || Number(sec?.units?.i1) || 0;
        const inertiaB = Number(sec?.units?.iY) || Number(sec?.units?.i2) || 0;

        // By definition the smaller inertia is the weak axis, the larger the stiff axis.
        const I_weak = Math.min(inertiaA, inertiaB);
        const I_stiff = Math.max(inertiaA, inertiaB);

        // Effective-length factors: betas_[0] -> weak axis, betas_[1] -> stiff axis.
        const beta_weak = Number(betas_[0]?.[0]);
        const beta_stiff = Number(betas_[1]?.[0]);

        // Guard every denominator: only a positive, finite value is usable.
        const isPositive = (x: number) => Number.isFinite(x) && x > 0;

        // Compute Pcr only when L, beta and I are all valid; otherwise leave that
        // direction at 0 ("not available") so it is dropped from the governing min.
        const eulerPcr = (I: number, beta: number) =>
          isPositive(L) && isPositive(beta) && isPositive(I) && E > 0
            ? (Math.PI * Math.PI * E * I) / Math.pow(beta * L, 2)
            : 0;

        Pcr_weak = eulerPcr(I_weak, beta_weak);
        Pcr_stiff = eulerPcr(I_stiff, beta_stiff);

        // console.log("Pcr_weak: ", Pcr_weak, " Pcr_stiff: ", Pcr_stiff);
        // console.log("beta_weak: ", beta_weak, " beta_stiff: ", beta_stiff);

        // Governing critical load is the smaller valid (non-zero) of the two.
        const validPcr = [Pcr_weak, Pcr_stiff].filter((p) => p > 0);
        Pcr = validPcr.length > 0 ? Math.min(...validPcr) : 0;
      }
    }

    if (secOrder && Math.abs(maxN) > 0.8 * Pcr && Pcr > 0) {
      AlertMessage(
        "warning_",
        t("warning_.label"),
        "",
        t("near_critical_load_.label") + " - 80% Pcr: " + ((0.8 * Pcr) / 1e3).toFixed(2) + " kN",
      );
    }

    if (secOrder && Math.abs(maxN) >= Pcr && Pcr > 0) {
      //clear all results
      setDisplacement({});
      setSupportForces({});
      setElementResultsForces({});
      setDisplacementSecondary({});
      setElementResultsForcesSecondary({});
      setDesignResult({});
      setCodeCheckResultExists(false);
      dispatch(setCriticalLoadExceeded(true));
      AlertMessage(
        "error_",
        t("error_.label"),
        "",
        t("critical_load_exceeded_.label") + " - Pcr: " + (Pcr / 1e3).toFixed(2) + " kN",
      );
    } else {
      setDisplacement(displacements_);
      setSupportForces(supportForce_);
      setElementResultsForces(elementResultsForce_);
      setDisplacementSecondary(displacementsSecondary_);
      setElementResultsForcesSecondary(elementResultsForceSecondary_);
      setDesignResult(designResults_);
      setCodeCheckResultExists(codeCheckResultExists_);
      dispatch(setCriticalLoadExceeded(false));
    }

    setBucklingMode(bucklingMode_);
    setBetas(betas_);

    const didCodeCheckJustSucceed = previousCodeCheckStatus.current === "loading" && codeCheckStatus === "succeeded";
    if (didCodeCheckJustSucceed) {
      setIsRightPanelOpen(true);
      // New results should always be shown directly, even from the design view.
      setRightPanelTab("code-check");
      if (shouldPromptForSurvey && !surveyPromptAt && !surveyPromptRequestedRef.current) {
        surveyPromptRequestedRef.current = true;
        void scheduleSurveyPrompt(Date.now() + SURVEY_DELAY_MS);
      }
    }

    if (selLC !== "-1") {
      const lcoGuid = selLC.replace("envelope_", "");
      const lcoInfo = getLoadcomb(lcoGuid, structure_);
      if (lcoInfo !== undefined && lcoInfo !== null && lcoInfo.length > 0) {
        const lco = lcoInfo?.[0] as ng.loadcombination.Data_json;
        if (lco !== null) {
          let lcoobj = ng.loadcombination.Data.fromObject(lco);
          setIsUlsComb(isULS(lcoobj));
          setIsSlsComb(isSLS(lcoobj));
        }
      } else {
        setIsUlsComb(false);
        setIsSlsComb(false);
      }
    } else {
      setIsUlsComb(false);
      setIsSlsComb(false);
    }
  }, [
    selProject?.data?.data?.output,
    stageSupports_,
    selLC,
    structure_,
    codeCheckStatus,
    showSecOrder,
    showBothOrders,
    selProject?.data?.data?.input,
  ]);

  useEffect(() => {
    previousCodeCheckStatus.current = codeCheckStatus;
  }, [codeCheckStatus]);

  // A finished analysis decides which result set the bottom bar starts on:
  // 2nd order alone when it was considered in the calculation settings, 1st order otherwise.
  useEffect(() => {
    const didAnalysisJustSucceed = previousAnalysisStatus.current === "loading" && analysisStatus === "succeeded";
    if (didAnalysisJustSucceed) {
      setShow2ndOrder(secOrder);
      setShow1stOrder(!secOrder);
    }
    previousAnalysisStatus.current = analysisStatus;
  }, [analysisStatus, secOrder]);

  // A first-order-only analysis has no 2nd order results to show or hide - lock the diagrams to
  // 1st order regardless of what a previous (2nd order) project may have left the toggles at.
  useEffect(() => {
    if (!secOrder) {
      setShow1stOrder(true);
      setShow2ndOrder(false);
    }
  }, [secOrder]);

  // Opening a project whose stored analysis holds 2nd order results starts on 2nd order alone.
  // Declared after the lock effect above so it wins when both fire on the same project load.
  const secOrderAppliedForGuid = useRef<string | undefined>(undefined);
  useEffect(() => {
    const guid = selProject?.guid;
    if (!guid || secOrderAppliedForGuid.current === guid) return;
    secOrderAppliedForGuid.current = guid;

    const lcombs = selProject?.data?.data?.output?.lcombs ?? [];
    const has2ndOrderResults = lcombs.some((lc) =>
      lc?.elementResults?.some((el) => el?.result?.positions?.some((pos) => !!pos?.results2nd?.force?.value?.length)),
    );
    if (has2ndOrderResults) {
      setShow1stOrder(false);
      setShow2ndOrder(true);
    }
  }, [selProject?.guid]);

  useEffect(() => {
    if (isFireAccordionOpen && !previousIsFireAccordionOpen.current) {
      dispatch(setShowSectionFireIndicator(true));
    }

    if (isFireAccordionOpen && !hasFireLoadCombination && showSectionFireIndicator) {
      dispatch(setShowSectionFireIndicator(false));
    }

    if (!isFireAccordionOpen && previousIsFireAccordionOpen.current && showSectionFireIndicator) {
      dispatch(setShowSectionFireIndicator(false));
    }

    previousIsFireAccordionOpen.current = isFireAccordionOpen;
  }, [dispatch, isFireAccordionOpen, hasFireLoadCombination, showSectionFireIndicator]);

  // A fire load combination newly appearing - added manually, produced by the load combination
  // generator, or present in a project being opened - turns the indicator on and opens the fire
  // accordion. Mirrors the "open accordion" trigger above but keyed off the combination list
  // instead of the accordion state. The reverse - the last fire combination being removed or
  // retyped away (e.g. changed from TYPE_FIRE to something else in the LCO generator) - turns the
  // indicator back off, regardless of accordion state. The accordion itself is never forced shut:
  // without a fire combination it still opens to show the settings, with a notice explaining they
  // need one to take effect.
  useEffect(() => {
    const fireCombinationAppeared = hasFireLoadCombination && !previousHasFireLoadCombination.current;

    if (fireCombinationAppeared && !showSectionFireIndicator) {
      dispatch(setShowSectionFireIndicator(true));
    }

    if (fireCombinationAppeared && !isFireAccordionOpen) {
      dispatch(setAccordionKeys([...activeAccordionKeys, "8"]));
    }

    if (!hasFireLoadCombination && previousHasFireLoadCombination.current && showSectionFireIndicator) {
      dispatch(setShowSectionFireIndicator(false));
    }

    previousHasFireLoadCombination.current = hasFireLoadCombination;
  }, [dispatch, hasFireLoadCombination, showSectionFireIndicator]);

  // Opening the Buckling accordion turns the buckling shape on in the section/side views,
  // closing it turns it back off. Mirrors the fire indicator behaviour above.
  useEffect(() => {
    if (secOrder && showSectionBuckling) {
      dispatch(setShowSectionBuckling(false));
    }

    if (isBucklingAccordionOpen && !previousIsBucklingAccordionOpen.current) {
      if (!secOrder) {
        dispatch(setShowSectionBuckling(true));
      }
    }

    if (!isBucklingAccordionOpen && previousIsBucklingAccordionOpen.current && showSectionBuckling) {
      dispatch(setShowSectionBuckling(false));
    }

    previousIsBucklingAccordionOpen.current = isBucklingAccordionOpen;
  }, [dispatch, isBucklingAccordionOpen, secOrder, showSectionBuckling]);

  /** FUNCTIONS BELOW */

  // Accordion handlers
  const handleToggleAccordionItem = (key: string) => {
    dispatch(toggleAccordionKey(key));
  };

  const handleAccordionDoubleClick = () => {
    dispatch(toggleCollapseAllAccordion());
  };

  // Helper: returns true if both this and a neighbor are open
  // Returns class for neighbor-open-top/bottom depending on which neighbor is open
  // styling set in SXAccordion.scss
  const getNeighborOpenClass = (index: number, activeKeys: string[]) => {
    const idxStr = index.toString();

    const isOpen = activeKeys.includes(idxStr);
    const aboveOpen = activeKeys.includes((index - 1).toString());
    const belowOpen = activeKeys.includes((index + 1).toString());

    // NEW: closed item with open neighbor above
    if (!isOpen && aboveOpen) return "neighbor-above-open";

    // Existing behavior for open items
    if (!isOpen) return "";
    if (aboveOpen && belowOpen) return "neighbor-open-top neighbor-open-bottom";
    if (aboveOpen) return "neighbor-open-top";
    if (belowOpen) return "neighbor-open-bottom";
    return "";
  };

  const handleDelete = (item: MyRowData) => {
    // setRows((prev) => prev.filter((r) => r.id !== item.id));
  };

  const handleAdd = () => {
    /*setRows((prev) => [
      ...prev,
      { id: Date.now().toString(), name: "New", value: 0, value2: 0 },
    ]);
    */
  };

  const isDebugAccordionOpen = activeAccordionKeys.includes("10");

  return (
    <>
      <Container fluid id="app_container">
        {isMobile && <SXMobileWarning />}
        {/* Busy overlay for analyse / code check / design — covering the whole window locks every
            input in the app while any of the three runs, not just the panel that started it. The
            design panel still shows its own local overlay for the prep step before the thunk
            dispatches (see MemberDesignPanel); this one takes over the moment designStatus flips
            to "loading", the same instant the other two already relied on. */}
        {isCalculating && (
          <SXCalcProgressOverlay
            variant="app"
            show
            fallbackLabelKey={
              analysisStatus === "loading"
                ? "progress_analysis_"
                : codeCheckStatus === "loading"
                  ? "progress_codecheck_"
                  : "progress_design_"
            }
            fallbackLabel={
              analysisStatus === "loading"
                ? "Running analysis…"
                : codeCheckStatus === "loading"
                  ? "Running code check…"
                  : "Running design…"
            }
          />
        )}
        {/* Top Navigation Bar */}
        <Navbar id="top-nav-bar" className="top-nav-bar-bg">
          <Navbar.Brand style={{ width: 115 }}>
            <SimplexSvgLogo />
          </Navbar.Brand>

          <Nav>
            <a href="/" id="app-title">
              Column
            </a>
          </Nav>
          <Nav className="ms-3">
            <Nav.Link>
              <button
                className="navbar-btn-slim"
                style={{ width: "auto", paddingBottom: 3 }}
                onClick={handeOpenSimplexCenter}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                {t("general.my_files")}
              </button>
            </Nav.Link>
          </Nav>
          <Nav className="ms-3">
            {/* user && <span className="title-divider">{/* <strong>{t("general.file")} :</strong> </span>*/}
            {
              user && selProject?.name === "tempFile" && (
                <span className="pointer" style={{ color: "#fff" }} onClick={() => dispatch(openModal("saveAsModal"))}>
                  {t("click_to_save_.label")}
                </span>
              ) // Replace with actual project name
            }
            {
              user && selProject?.name !== "tempFile" && (
                <span style={{ color: "#fff" }} onClick={() => {}}>
                  {selProject?.name}
                </span>
              ) // Replace with actual project name
            }
          </Nav>
          <Nav className="me-auto"></Nav>
          <Nav>
            {" "}
            {user && (
              <OverlayTrigger
                placement="bottom"
                delay={{ show: 700, hide: 200 }}
                overlay={<Tooltip id="tooltip">{t("general.fullscreen")}</Tooltip>}
              >
                <Nav.Link onClick={() => dispatch(toggleFullscreenThunk())}>
                  <FullScreenToggleIcon isFullscreen={isFullscreen} />
                </Nav.Link>
              </OverlayTrigger>
            )}
          </Nav>
          <Nav>
            {user && (
              <Nav.Link
                className="hover-underline-animation"
                id={"showsettingsdialog"}
                onClick={() => {
                  // open settingsmodal
                  dispatch(openModal("settingsModal"));
                }}
              >
                <OverlayTrigger
                  placement="bottom"
                  delay={{ show: 700, hide: 200 }}
                  overlay={<Tooltip id="tooltip">{t("settings_.label")}</Tooltip>}
                >
                  <SettingsIcon color="#fff" />
                </OverlayTrigger>
              </Nav.Link>
            )}
          </Nav>
          <Nav>
            {user && (
              <Nav.Link onClick={() => dispatch(openOffCanvas())} style={{ paddingTop: 8 }}>
                <UserInitialsIcon user={user} />
              </Nav.Link>
            )}
          </Nav>
        </Navbar>

        {/* This is the offcanvas menu that will be shown when the user clicks on UserInitialsIcon */}
        <SXOffCanvasMenu
          user={user}
          showOffCanvas={showOffCanvas}
          handleCloseOffCanvas={() => dispatch(closeOffCanvas())}
        />

        {/* Main Application Row */}
        <Row id="main-app-row" style={{ height: "100%" }}>
          {/* Floating indicator visible when left panel is collapsed */}
          {!isLeftPanelOpen && (
            <div
              className="panel-indicator left"
              onClick={() => setIsLeftPanelOpen(true)}
              title={t("input_.label", "Open input")}
            >
              <span>{t("input_.label", "Input")}</span>
            </div>
          )}

          {/* Left Panel Toggle Button (always visible) */}
          <button
            className={`left-panel-toggle${isLeftPanelOpen ? " open" : ""}`}
            style={{
              left: isLeftPanelOpen ? LEFT_PANEL_WIDTH + 10 : COLLAPSED_PANEL_WIDTH + 12,
            }}
            onClick={() => setIsLeftPanelOpen(!isLeftPanelOpen)}
          >
            <span className="toggle-icon">{isLeftPanelOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}</span>
          </button>
          {/* -------------------------------------------------------------- */}
          {/* -------------------- LEFT PANEL (INPUT ) --------------------- */}
          {/* -------------------------------------------------------------- */}

          <div
            className={`left-panel${isLeftPanelOpen ? " open" : ""}`}
            style={{
              left: isLeftPanelOpen ? 0 : -(LEFT_PANEL_WIDTH + 20),
              width: LEFT_PANEL_WIDTH,
            }}
          >
            {/* Left Panel Content */}
            <Row className="left-panel-content">
              <Col className="left-panel-content-col">
                <Row className="left-panel-header">
                  <Col>
                    <span className="panel-title">{t("input_.label")}</span>
                  </Col>
                </Row>
                {isLeftPanelOpen && (
                  <Accordion
                    activeKey={activeAccordionKeys}
                    style={{ paddingBottom: 80 }}
                    onSelect={(key) => handleToggleAccordionItem(key as string)}
                  >
                    {/* --------------------ACCORDION ITEM #1 Code Settings (key=0) --------------------- */}
                    <Accordion.Item eventKey="0" className={getNeighborOpenClass(0, activeAccordionKeys)}>
                      <Accordion.Header id={"accordion_code_settings"} onDoubleClick={handleAccordionDoubleClick}>
                        {t("code_settings_.label")}
                      </Accordion.Header>
                      <Accordion.Body>
                        <Container>
                          <SXCodeSettings />
                        </Container>
                      </Accordion.Body>
                    </Accordion.Item>
                    {/* --------------------ACCORDION ITEM #2 Geometry (key=1) --------------------- */}
                    <Accordion.Item eventKey="1" className={getNeighborOpenClass(1, activeAccordionKeys)}>
                      <Accordion.Header id={"accordion_geometry"} onDoubleClick={handleAccordionDoubleClick}>
                        {t("geometry_.label")}
                      </Accordion.Header>
                      <Accordion.Body>
                        <Container>
                          <SXInputGeometry />
                        </Container>
                      </Accordion.Body>
                    </Accordion.Item>
                    {/* --------------------ACCORDION ITEM #3 Section and Material (key=2) --------------------- */}
                    <Accordion.Item eventKey="2" className={getNeighborOpenClass(2, activeAccordionKeys)}>
                      <Accordion.Header
                        id={"accordion_section_and_material"}
                        onDoubleClick={handleAccordionDoubleClick}
                      >
                        {t("section_and_material_.label")}
                      </Accordion.Header>
                      <Accordion.Body>
                        <Container>
                          <ProjectInputSectionExplorer activeKeys={activeAccordionKeys} />
                        </Container>
                      </Accordion.Body>
                    </Accordion.Item>
                    {/* --------------------ACCORDION ITEM #4 Reinforcement (key=3) --------------------- */}
                    {element_.beam?.concrete && (
                      <Accordion.Item eventKey="3" className={getNeighborOpenClass(3, activeAccordionKeys)}>
                        <Accordion.Header id={"accordion_reinforcement"} onDoubleClick={handleAccordionDoubleClick}>
                          {t("reinforcement_.label")}
                        </Accordion.Header>
                        <Accordion.Body>
                          <Container>
                            <SXInputReinforcement />
                          </Container>
                        </Accordion.Body>
                      </Accordion.Item>
                    )}
                    {/* --------------------ACCORDION ITEM #5 Buckling (key=4) --------------------- */}
                    <Accordion.Item eventKey="4" className={getNeighborOpenClass(4, activeAccordionKeys)}>
                      <Accordion.Header id={"accordion_buckling"} onDoubleClick={handleAccordionDoubleClick}>
                        {t("buckling_.label")}
                      </Accordion.Header>
                      <Accordion.Body>
                        {/* SXBuckling handles second order itself: it hides only the flexural
                            buckling lengths, and lateral torsional buckling stays (#433). */}
                        <Container>
                          <SXBuckling />
                        </Container>
                      </Accordion.Body>
                    </Accordion.Item>
                    {/* --------------------ACCORDION ITEM #6 Load cases (key=5) --------------------- */}
                    <Accordion.Item eventKey="5" className={getNeighborOpenClass(5, activeAccordionKeys)}>
                      <Accordion.Header id={"accordion_loadcases"} onDoubleClick={handleAccordionDoubleClick}>
                        {t("lca_name_.label")}
                      </Accordion.Header>
                      <Accordion.Body>
                        <Container>
                          {/* todo(Gustav): should SXProjectInputLoadCases include factorUpdate? why is that an option that defaults to false? does that makes sense??? */}
                          <SXProjectInputLoadCases />
                        </Container>
                      </Accordion.Body>
                    </Accordion.Item>
                    {/* --------------------ACCORDION ITEM #7 Loads (key=6) --------------------- */}
                    <Accordion.Item eventKey="6" className={getNeighborOpenClass(6, activeAccordionKeys)}>
                      <Accordion.Header id={"accordion_loads"} onDoubleClick={handleAccordionDoubleClick}>
                        {t("loads_.label")}
                      </Accordion.Header>
                      <Accordion.Body>
                        <Container>
                          <SXInputLoads />
                        </Container>
                      </Accordion.Body>
                    </Accordion.Item>
                    {/* --------------------ACCORDION ITEM #7 Load combinations (key=7) --------------------- */}
                    <Accordion.Item eventKey="7" className={getNeighborOpenClass(7, activeAccordionKeys)}>
                      <Accordion.Header id={"accordion_loadcombinations"} onDoubleClick={handleAccordionDoubleClick}>
                        {t("loadcombs_.label")}
                      </Accordion.Header>
                      <Accordion.Body>
                        <Container>
                          {/* Content for Accordion Item 7 */}
                          <SXProjectInputLoadCombinations />
                        </Container>
                      </Accordion.Body>
                    </Accordion.Item>
                    {/* --------------------ACCORDION ITEM #8 Fire settings (key=8) --------------------- */}
                    <Accordion.Item eventKey="8" className={getNeighborOpenClass(8, activeAccordionKeys)}>
                      <Accordion.Header id={"accordion_fire_settings"} onDoubleClick={handleAccordionDoubleClick}>
                        {t("fire_settings_.label")}
                      </Accordion.Header>
                      <Accordion.Body>
                        <Container>
                          <SXInputFireSettings />
                        </Container>
                      </Accordion.Body>
                    </Accordion.Item>
                    {/* --------------------ACCORDION ITEM #9 Calculation settings (key=9) --------------------- */}
                    <Accordion.Item eventKey="9" className={getNeighborOpenClass(9, activeAccordionKeys)}>
                      <Accordion.Header
                        id={"accordion_calculation_settings"}
                        onDoubleClick={handleAccordionDoubleClick}
                      >
                        {t("calculation_settings_.label")}
                      </Accordion.Header>
                      <Accordion.Body>
                        <Container>
                          <SXInputCalculation />
                        </Container>
                      </Accordion.Body>
                    </Accordion.Item>

                    {/* --------------------ACCORDION ITEM #10 Debug — project slice JSON (dev only) --------------------- */}
                    {isDevConfig && (
                      <Accordion.Item eventKey="10" className={getNeighborOpenClass(10, activeAccordionKeys)}>
                        <Accordion.Header id={"accordion_debug_project"} onDoubleClick={handleAccordionDoubleClick}>
                          🛠 Debug: Project slice (dev only)
                        </Accordion.Header>
                        <Accordion.Body>{isDebugAccordionOpen && <JSONDebugPrint value={selProject} />}</Accordion.Body>
                      </Accordion.Item>
                    )}
                    {isDevConfig && (
                      <Accordion.Item eventKey="11" className={getNeighborOpenClass(11, activeAccordionKeys)}>
                        <Accordion.Header id="accordion_debug_3d_view" onDoubleClick={handleAccordionDoubleClick}>
                          🛠 3D view (dev only)
                        </Accordion.Header>
                        <Accordion.Body>
                          <SX3DViewSettings />
                        </Accordion.Body>
                      </Accordion.Item>
                    )}
                  </Accordion>
                )}
              </Col>
            </Row>
          </div>
          {/* ---------------------------------------------------------------------------------------------- */}
          {/* -------------------- CENTER : MAIN CONTENT AREA (GRAPHS, VIEWPORT, ETC.) --------------------- */}
          {/* ------------------------------------------------------------------------------------- */}
          <Col
            id="viewport-col"
            style={{
              height: "100%",
              overflow: "auto",
              marginLeft: isLeftPanelOpen ? LEFT_PANEL_WIDTH + 40 : COLLAPSED_PANEL_WIDTH + 40,
              marginRight: isRightPanelOpen ? RIGHT_PANEL_WIDTH + 20 : COLLAPSED_PANEL_WIDTH + 20,
              transition: "margin-left 0.3s ease-in-out",
              background: "transparent",
            }}
          >
            {/* Main Content Area */}
            <Viewport
              results={{
                elementForces: elementResultsForces,
                supportForces: supportForces,
                displacements: displacement,
                elementForcesSecondary: elementResultsForcesSecondary,
                displacementsSecondary: displacementSecondary,
                bucklingMode: bucklingMode,
                design: designResult,
                betas: betas,
              }}
              selectedLcombGuid={selLC}
              graphPointsMode={graphPointsMode}
              orderResultsLabel={hasAnalysisOutput ? orderResultsLabel : undefined}
            />
            {/* Carbon footprint readout, floating over the top-right of the viewport. Kept
                outside the results panel so the figure stays visible while the input changes;
                the offset follows the same panel arithmetic as the toolbar below. */}
            <SXCarbonFootprint
              right={isRightPanelOpen ? RIGHT_PANEL_WIDTH + 20 : COLLAPSED_PANEL_WIDTH + 20}
              project={selProject}
              carbonStatus={useAppSelector(selectCarbonStatus)}
            />
            {/* ------------------------------------------------------------ */}
            {/* -------------------- BOTTOM BUTTON BAR --------------------- */}
            {/* ------------------------------------------------------------ */}
            <ButtonToolbar
              className="sx-toolbar"
              style={{
                left: isLeftPanelOpen ? LEFT_PANEL_WIDTH : COLLAPSED_PANEL_WIDTH,
                right: isRightPanelOpen ? RIGHT_PANEL_WIDTH + 10 : 10,
              }}
            >
              <ButtonGroup id="viewport-btns" className="sx-button-group sx-btn-group-connected">
                <Button
                  id="btn-xy"
                  className={`tab-style-btn  ${viewportState === "xy" ? "active" : ""}`}
                  title="XY"
                  onClick={(e) => {
                    dispatch(setViewportState("xy"));
                  }}
                >
                  XY
                </Button>
                <Button
                  id="btn-xz"
                  className={`tab-style-btn  ${viewportState === "xz" ? "active" : ""}`}
                  title="XZ"
                  onClick={(e) => {
                    dispatch(setViewportState("xz"));
                  }}
                >
                  XZ
                </Button>
                <Button
                  id="btn-splitview"
                  className={`tab-style-btn ${viewportState === "xy+xz" ? "active" : ""}`}
                  title={t("split_view_.label")}
                  onClick={(e) => {
                    dispatch(setViewportState("xy+xz"));
                  }}
                >
                  <SplitViewIcon size={16} strokeWidth={4} />
                </Button>
                <Button
                  id="btn-3d"
                  className={`tab-style-btn tab-style-btn-3d ${viewportState === "3d" ? "active" : ""}`}
                  title={t("isometric_view_beta_tooltip_.label")}
                  onClick={(e) => {
                    dispatch(setViewportState("3d"));
                  }}
                >
                  <Cube5 size={16} strokeWidth={2} />

                  <span className="toolbar-beta-badge">β</span>
                </Button>
              </ButtonGroup>

              <ButtonGroup className="sx-button-group">
                {showAnalysisButton && !user.settings.analyseOnSave && (
                  <Button
                    id="project_result_run_analysis"
                    className="action-btn"
                    title=""
                    disabled={isCalculating}
                    onClick={async (e) => {
                      try {
                        if (selProject !== null) {
                          // Remove alerts (and their duplicate-suppression markers) from
                          // the previous run so run-specific messages such as the near-
                          // critical / critical-load warnings can be shown again.
                          clearAlerts();
                          // 2nd order effects are produced by the normal force acting through the
                          // lateral deflection, so a combination with no vertical point load gets
                          // none at all - warn before the run rather than let the user read a
                          // result that is silently 1st order. See secondOrderWarnings.
                          checkSecondOrderVerticalPointLoad(structure_, secOrder, dispatch);
                          await analyse(selProject);
                        }
                      } catch (x) {
                        report_exception(t, x, t("analysis_.label"));
                      }
                    }}
                  >
                    <span>{analysisStatus === "loading" ? "..." : t("analysis_.label")}</span> <PlayIcon size={14} />
                  </Button>
                )}

                <Button
                  id="project_result_run_code_check"
                  className="primary-btn"
                  title={t("run_code_check_.label")}
                  disabled={!hasUsableAnalysisOutput || isCalculating}
                  onClick={(e) => {
                    if (selProject !== null) {
                      codeCheck(selProject);
                    }
                  }}
                >
                  <span>{codeCheckStatus === "loading" ? "..." : t("code_check_.label")}</span>{" "}
                  <CheckMarkIcon size={14} />
                </Button>

                {/* Design button hidden for now
                {isDevConfig && (
                  <Button
                    id="project_result_run_design"
                    className="primary-btn"
                    title="Run auto-design of the current column"
                    disabled={!hasUsableAnalysisOutput || designStatus === "loading"}
                    onClick={(e) => {
                      if (selProject !== null) {
                        design(selProject);
                      }
                    }}
                  >
                    <span>{designStatus === "loading" ? "..." : t("design_.label", "Design")}</span> <SettingsIcon size={14} />
                  </Button>
                )}
                */}

                {/* <Button
                  id="project_result_run_code_check"
                  className="secondary-btn"
                  disabled={!selProject}
                  title="results"
                  onClick={(e) => {
                    dispatch(openModal("resultTableModal"));
                  }}
                >
                  <span>{t("results_.label")}</span> <EqualsIcon size={14} />
                </Button> */}

                {/* The reason sits on the Dropdown's own wrapper div, not only on the toggle: a
                    disabled <button> dispatches no pointer events, so its title never surfaces —
                    precisely when there is something to say. The wrapper is not disabled, so the
                    tooltip shows when hovering the greyed-out button. */}
                <Dropdown id="print-dropdown" className="print-dropdown" title={printBlockedReason || undefined}>
                  <Dropdown.Toggle
                    className="secondary-btn"
                    title={printBlockedReason || t("print_.label")}
                    disabled={!selProject || isPrinting || !!printBlockedReason}
                  >
                    <PrintIcon size={16} />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => handlePrint("reduced")} disabled={isPrinting}>
                      {t("print_extent_minimum_.label")}
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handlePrint("normal")} disabled={isPrinting}>
                      {t("print_extent_normal_.label")}
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handlePrint("full")} disabled={isPrinting}>
                      {t("print_extent_extended_.label")}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </ButtonGroup>

              <Col sm={2} className="sx-toolbar-lco-col ps-0 px-0">
                <SXDropdown
                  id="lco-dropdown"
                  toggleId="lco_dropdown"
                  className="sx-dropdown"
                  dropDirection="up"
                  dataTestId="dropdown_lco"
                  name="lco"
                  style={{ paddingLeft: 10, backgroundColor: "var(--lco-dropdown-bg, #fff)" }}
                  lang={lang}
                  list={[...loadcombinationDropdownItems]}
                  sections={lcoSections}
                  val={selectedLcoItem}
                  notSelectedName={lcoEmptyKey}
                  onUpdate={(value) => value && setSelLC(value.id)}
                  rawNames
                  localisation={false}
                  itemClassName="d-flex justify-content-between align-items-center"
                  renderItemSuffix={(item) =>
                    selLC === item.id ? <CheckMarkIcon size={12} /> : <span style={{ width: 12 }}></span>
                  }
                />
              </Col>

              <Dropdown id="visibility-dropdown" className="visibility-dropdown" align="end">
                <Dropdown.Toggle className="secondary-btn" title={t("show_.label")}>
                  <ViewIcon size={16} />
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ minWidth: 200 }}>
                  <Dropdown.Header>{t("settings_header_graphs_.label")}</Dropdown.Header>
                  <div className="px-3 py-1 d-flex flex-column gap-1">
                    <Form.Check
                      type="radio"
                      className="sx-switch text-nowrap"
                      id="graph-points-off"
                      name="graphPointsMode"
                      label={t("graph_point_hide_.label")}
                      checked={graphPointsMode === "off"}
                      onChange={() => setGraphPointsMode("off")}
                    />
                    <Form.Check
                      type="radio"
                      className="sx-switch text-nowrap"
                      id="graph-points-max"
                      name="graphPointsMode"
                      label={t("graph_point_max_min_.label")}
                      checked={graphPointsMode === "max"}
                      onChange={() => setGraphPointsMode("max")}
                    />
                    <Form.Check
                      type="radio"
                      className="sx-switch text-nowrap"
                      id="graph-points-all"
                      name="graphPointsMode"
                      label={t("graph_point_all_.label")}
                      checked={graphPointsMode === "all"}
                      onChange={() => setGraphPointsMode("all")}
                    />
                  </div>

                  <Dropdown.Divider />

                  <div className="px-3 py-1 d-flex flex-column gap-1">
                    <Form.Check
                      type="switch"
                      className="sx-switch text-nowrap"
                      id="btn-show-buckling-length"
                      label={t("show_buckling_shape_.label")}
                      checked={secOrder ? false : showSectionBuckling}
                      disabled={secOrder}
                      onChange={() => dispatch(toggleShowSectionBuckling())}
                    />
                    <Form.Check
                      type="switch"
                      className="sx-switch text-nowrap"
                      id="btn-show-dimensions"
                      label={t("show_dimensions_.label")}
                      checked={showSectionDimensions}
                      onChange={() => dispatch(toggleShowSectionDimensions())}
                    />
                    <Form.Check
                      type="switch"
                      className="sx-switch text-nowrap"
                      id="btn-show-support-reactions"
                      label={t("show_reactions_.label")}
                      checked={showSectionReactions}
                      onChange={() => dispatch(toggleShowSectionReactions())}
                    />
                    <Form.Check
                      type="switch"
                      className="sx-switch text-nowrap"
                      id="btn-show-fire-indicator"
                      label={t("show_fire_exposure_.label")}
                      checked={showSectionFireIndicator}
                      disabled={isFireIndicatorDisabled}
                      onChange={() => dispatch(toggleShowSectionFireIndicator())}
                    />
                  </div>

                  <Dropdown.Divider />

                  <div id="order-results" className="px-3 py-1 d-flex flex-column gap-1">
                    <Form.Check
                      type="radio"
                      className="sx-switch text-nowrap"
                      id="btn-show-1st-order-results"
                      name="result-order"
                      label={t("1st_order_.label")}
                      checked={show1stOrder && !show2ndOrder}
                      disabled={!secOrder || !hasAnalysisOutput}
                      onChange={() => {
                        setShow1stOrder(true);
                        setShow2ndOrder(false);
                      }}
                    />
                    <Form.Check
                      type="radio"
                      className="sx-switch text-nowrap"
                      id="btn-show-2nd-order-results"
                      name="result-order"
                      label={t("2nd_order_.label")}
                      checked={show2ndOrder && !show1stOrder}
                      disabled={!secOrder || !hasAnalysisOutput}
                      onChange={() => {
                        setShow1stOrder(false);
                        setShow2ndOrder(true);
                      }}
                    />
                    <Form.Check
                      type="radio"
                      className="sx-switch text-nowrap"
                      id="btn-show-both-orders-results"
                      name="result-order"
                      label={t("both_.label")}
                      checked={show1stOrder && show2ndOrder}
                      disabled={!secOrder || !hasAnalysisOutput}
                      onChange={() => {
                        setShow1stOrder(true);
                        setShow2ndOrder(true);
                      }}
                    />
                  </div>
                </Dropdown.Menu>
              </Dropdown>
            </ButtonToolbar>
          </Col>

          {/* Floating indicator visible when right panel is collapsed */}
          {!isRightPanelOpen && (
            <div
              className="panel-indicator right"
              onClick={() => setIsRightPanelOpen(true)}
              title={t("open_results_.label", "Open results")}
            >
              <span>{t("code_check_and_design_.label")}</span>
            </div>
          )}

          {/* Right Panel Toggle Button (always visible) */}
          <button
            id="right-panel-toggle-icon"
            className={`right-panel-toggle${isRightPanelOpen ? " open" : ""}`}
            style={{
              right: isRightPanelOpen ? 600 + 10 : 52,
            }} // alignment with panel and visual connection when open
            onClick={() => setIsRightPanelOpen(!isRightPanelOpen)}
          >
            <span className="toggle-icon">{isRightPanelOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />}</span>
          </button>
          {/* ---------------------------------------------------------------- */}
          {/* -------------------- RIGHT PANEL (RESULTS) --------------------- */}
          {/* ---------------------------------------------------------------- */}
          <div
            className={`right-panel${isRightPanelOpen ? " open" : ""}`}
            style={{
              position: "fixed",
              right: isRightPanelOpen ? 0 : -(RIGHT_PANEL_WIDTH + 20),
              zIndex: 1020,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="right-panel-content" style={{ flex: 1 }}>
              <Container className="p-0">
                <Row>
                  <Col>
                    <Row className="right-panel-header mb-2">
                      <Col>
                        <span className="panel-title">{t("code_check_and_design_.label")}</span>
                      </Col>
                    </Row>
                    <Row className="mx-2">
                      <Col>
                        <Tabs
                          activeKey={rightPanelTab}
                          onSelect={(key) => setRightPanelTab(key ?? "code-check")}
                          id="rightpanel-tabs"
                          className="simplex-custom-tabs"
                        >
                          <Tab eventKey="code-check" title={t("code_check_.label", "Code Check")}>
                            <ControlsReport selLC={selLC} onSelLCChange={setSelLC} />
                          </Tab>
                          <Tab eventKey="design" title={t("member_design_.label", "Member Design")}>
                            <MemberDesignPanel
                              selLC={selLC}
                              // Approving re-runs the analysis and the code check over what was
                              // applied, so bring up the tab those results land in instead of
                              // leaving the user on the design settings.
                              onDesignApproved={() => setRightPanelTab("code-check")}
                            />
                          </Tab>
                        </Tabs>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Container>
            </div>
          </div>

          {/** modals can be placed at the bottom */}
          <SettingsModal analyse={analyse} isSettingsModalOpen={isSettingsModalOpen} />
          <>
            {selProject && (
              <ResultTableModal
                isModalOpen={isResultTableModalOpen}
                isUlsComb={isUlsComb}
                isSlsComb={isSlsComb}
                codeCheckResultExists={codeCheckResultExists}
                elementResultsForces={elementResultsForces}
                supportForces={supportForces}
                displacement={displacement}
                designResult={designResult}
              />
            )}
            <>
              {" "}
              {user && (
                <SaveAsModal
                  projects={user.projects}
                  isModalOpen={isSaveModalOpen}
                  close={() => {}}
                  onSave={async (userData: any) => {
                    return await saveTempProject(userData.name, userData.project);
                  }}
                />
              )}
              <AdminViewWrapperModal
                isModalOpen={isAdminViewModalOpen}
                close={() => dispatch(closeModal("adminViewModal"))}
              />
              <DisclaimerModal
                isModalOpen={isDisclaimerModalOpen}
                close={() => dispatch(closeModal("disclaimerModal"))}
                onDoNotShowAgainChange={async (showDisclaimer: boolean) => {
                  await hideDisclaimerModal(showDisclaimer);
                  dispatch(closeModal("disclaimerModal"));
                }}
              />
              {releaseNotes && (
                <>
                  <ReleaseNotesModal
                    show={isReleaseNotesModalOpen}
                    close={() => dispatch(closeModal("releaseNotesModal"))}
                    header={t("general.release_notes")}
                    releases={releaseNotes.releases}
                  />
                  <ReleaseNotesModal
                    show={isLatestReleaseNotesModalOpen}
                    close={async () => {
                      dispatch(closeModal("latestReleaseNotesModal"));
                      if (latestReleaseVersion) {
                        await markReleaseNotesSeen(latestReleaseVersion);
                      }
                    }}
                    header={t("general.news_in_latest_version")}
                    releases={releaseNotes.releases.slice(0, 1)}
                  />
                </>
              )}
              <SXSurveyDialog
                open={isSurveyModalOpen}
                onClose={() => {
                  resetscheduleSurveyPrompt(Date.now());
                  setUserDidCLoseSurvey(true);
                  dispatch(closeModal("surveyModal"));
                }}
                onComplete={async (payload: any) => {
                  // handle survey completione

                  await saveSurveyData(payload);
                }}
                surveyId={SURVEY_ID}
                onSave={async (user: any) => {
                  dispatch(closeModal("surveyModal"));
                  // handle survey save
                }}
              />
            </>
          </>
          <LCOGeneratoreModal isModalOpen={isLcoModalOpen} />
        </Row>
      </Container>
    </>
  );
}

export default App;
